import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { TabType } from '../types';
import { ChevronRight, Check, ChevronDown, Play, Pause, Trash2, ExternalLink } from 'lucide-react';
import playAllIcon from '../assets/icons/interactions/play-all.svg';
import { cn } from '@/lib/utils';
import { AutoScrollText } from './AutoScrollText';
import { useStagedProgress } from './useStagedProgress';
import {
  Variant,
  VariantGroup,
  Color,
  ColorGroup,
  Accessory,
  AccessoryGroup,
  Package,
  PackageGroup,
  useGetConfiguration,
  useGetActiveVariantIDs,
  useGetActiveColorIDs,
  useGetActiveAccessoryIDs,
  useGetActivePackageIDs,
  useGetActiveFeatureIDs,
  useAccessoryStore,
} from '@adloid-technologies/discover-sdk';
import { usePackageStore } from '@adloid-technologies/discover-sdk/dist/esm/store/package-store';
import { RemoveMultipleAccessoriesWithLoading, AddMultipleAccessoriesWithLoading, SetActiveAccessory } from '@adloid-technologies/discover-sdk/dist/esm/graphics-utils/accessory';
import { RemovePackage } from '@adloid-technologies/discover-sdk/dist/esm/graphics-utils/packages';
import { LoaderState } from '@adloid-technologies/discover-sdk/dist/esm/store/loader-store';
import { SwapSku } from '@adloid-technologies/discover-sdk/dist/esm/graphics-utils/sku';
import { HandleInteraction } from '@adloid-technologies/discover-sdk/dist/esm/graphics-utils/interactions';
import { InteractionStore } from '@adloid-technologies/discover-sdk/dist/esm/store/interactions-store';
import { setViewportLoaderText, getViewportLoaderText } from '../utils/viewportLoaderText';
import { ConfigurationStore } from '@adloid-technologies/discover-sdk/dist/esm/store/configuration-store';
import { useChangeDefaultConfigOnVariantChange } from '@adloid-technologies/discover-sdk/dist/esm/hooks/useChangeDefaultConfigOnVariantChange';
import { FeatureStore, useFeatureStore } from '@adloid-technologies/discover-sdk/dist/esm/store/feature-store';
import { useFeatureGroupContext, FeatureGroupContext } from '@adloid-technologies/discover-sdk/dist/esm/components/Feature/FeatureGroupContext';
import { HandleFeaturePause, HandleFeaturesAutoplay } from '@adloid-technologies/discover-sdk/dist/esm/graphics-utils/feature';
import { uimetadata, metadata, assetSrc } from '../utils/metadata';
import { getPrice, getDisplayPrice, arePricesVisible, isCarPriceVisible, formatPrice } from '../utils/pricing';
import { useTranslation, useRTL } from '../utils/translations';
import { useSelectedJourney, journeyOf } from '../utils/selectedJourney';
import { useLogEvent, AnalyticsIds } from '../utils/useAnalytics';

interface ConfigPanelProps {
  activeTab: TabType;
  isPreviewMode: boolean;
  setIsPreviewMode: (val: boolean) => void;
  isDarkMode: boolean;
  onPackageInfoClick?: (id: string) => void;
}

const AccordionItem: React.FC<{ title: string; children: React.ReactNode; isOpen: boolean; onToggle: () => void; isDarkMode: boolean; parent?: string; }> = ({ title, children, isOpen, onToggle, isDarkMode, parent = '' }) => {
  const { trackEvent } = useLogEvent();
  // Split title into label and optional count like "Exterior Colors (6)"
  const match = title.match(/^(.+?)\s*(\(\d+\))?$/);
  const label = match?.[1]?.trim() ?? title;
  const count = match?.[2] ?? '';
  const handleToggle = () => {
    trackEvent(AnalyticsIds.categoryInteract, {
      state: !isOpen,
      parent,
      primaryTypeName: label,
      secondaryTypeName: 'panel',
    });
    onToggle();
  };
  return (
    <div className="border-none">
      {/* text-start: a button defaults to text-align:center, which centres the
          wrapped lines of a long heading (e.g. "Carbon Ceramic Brakes with
          cross-drilled rotors"). start, not left, so RTL still reads right. */}
      <button onClick={handleToggle} className="w-full flex items-center justify-between py-0 mb-3 hover:no-underline text-start">
        <div className="flex items-center gap-[4px]">
          <h3 className={cn('font-sans text-[14px] md:text-[16px] font-bold leading-[20px] tracking-[0.35px] uppercase', isDarkMode ? 'text-white' : 'text-[#212121]')}>{label}</h3>
          {count && <span className={cn('font-sans text-[14px] font-medium leading-[20px] tracking-[0.3184px] uppercase', isDarkMode ? 'text-white/40' : 'text-[#adadad]')}>{count}</span>}
        </div>
        <ChevronDown className={cn('w-4 h-4 transition-transform', isDarkMode ? 'text-white/40' : 'text-[#999]', isOpen ? 'rotate-180' : '')} />
      </button>
      {isOpen && <div className="pb-3 md:pb-4">{children}</div>}
    </div>
  );
};

interface VariantCardProps { selected?: boolean; id?: string; data: any; isDarkMode: boolean; }
const VariantCard: React.FC<VariantCardProps> = ({ selected = false, data, isDarkMode }) => {
  const name = data?.name?.en ?? '';
  const desc = data?.description?.en ?? '';
  const price = getDisplayPrice(data?.price);
  return (
    <div className={cn(
      'relative h-[118px] cursor-pointer transition-all rounded-[var(--brand-card-radius)] overflow-hidden px-[18px] pt-[24px] pb-[14px] flex flex-col justify-between',
      selected
        ? 'border border-[var(--brand-accent)] bg-white'
        : isDarkMode
        ? 'border border-white/5 bg-[#111111]'
        : 'border border-[#f2f2f2] bg-[#f2f2f2]',
    )}>
      <div className="flex flex-col gap-[4px]">
        <h2 className={cn('font-sans text-[28px] font-black leading-tight tracking-tight', selected || !isDarkMode ? 'text-black' : 'text-white')}>{name}</h2>
        <span className={cn('text-[14px] font-medium', selected || !isDarkMode ? 'text-[#666]' : 'text-white/40')}>
          {price > 0 ? formatPrice(price, { sign: true }) : ''}
        </span>
      </div>
      {desc && <p className={cn('text-[12px] font-medium leading-snug', selected || !isDarkMode ? 'text-[#666]' : 'text-white/30')}>{desc}</p>}
      {selected && (
        <div className="absolute top-[10px] right-[12px] w-[14px] h-[14px] bg-[var(--brand-accent)] rounded-full flex items-center justify-center">
          <Check className="w-[9px] h-[9px] text-white stroke-[3]" />
        </div>
      )}
    </div>
  );
};

interface FilterOptionCardProps { isActive: boolean; option: any; isDarkMode: boolean; onClick: () => void; isRTL: boolean; }
const FilterOptionCard: React.FC<FilterOptionCardProps> = ({ isActive, option, isDarkMode, onClick, isRTL }) => {
  const name = isRTL ? (option.label?.ar ?? '') : (option.label?.en ?? '');
  const subtitle = isRTL ? (option.subtitle?.ar ?? '') : (option.subtitle?.en ?? '');
  const description = isRTL ? (option.description?.ar ?? '') : (option.description?.en ?? '');
  return (
    <div
      onClick={onClick}
      className={cn(
        'relative h-[118px] cursor-pointer transition-all rounded-[var(--brand-card-radius)] overflow-hidden px-[18px] pt-[24px] pb-[14px] flex flex-col justify-between',
        isActive
          ? 'border border-[var(--brand-accent)] bg-white'
          : isDarkMode
          ? 'border border-white/5 bg-[#111111] hover:border-white/10'
          : 'border border-[#f2f2f2] bg-[#f2f2f2] hover:border-gray-300',
      )}
    >
      <div className="flex flex-col gap-[4px]">
        <h2 className={cn('font-sans text-[28px] font-black leading-tight tracking-tight', isActive || !isDarkMode ? 'text-black' : 'text-white')}>{name}</h2>
        {subtitle && (
          <span className={cn('text-[14px] font-medium', isActive || !isDarkMode ? 'text-[#666]' : 'text-white/40')}>{subtitle}</span>
        )}
      </div>
      {description && (
        <p className={cn('text-[12px] font-medium leading-snug', isActive || !isDarkMode ? 'text-[#666]' : 'text-white/30')}>{description}</p>
      )}
      {isActive && (
        <div className="absolute top-[10px] right-[12px] w-[14px] h-[14px] bg-[var(--brand-accent)] rounded-full flex items-center justify-center">
          <Check className="w-[9px] h-[9px] text-white stroke-[3]" />
        </div>
      )}
    </div>
  );
};

interface ColorCardProps { selected?: boolean; id?: string; data: any; isDarkMode: boolean; }
const ColorCard: React.FC<ColorCardProps> = ({ selected = false, data, isDarkMode }) => {
  const { isRTL } = useRTL();
  const label = isRTL ? (data?.label?.ar ?? data?.label?.en ?? '') : (data?.label?.en ?? '');
  const image = data?.image ? assetSrc(data.image) : '';
  const hex = data?.hex as string | undefined;
  // Paint and interior trim are chargeable on the GM schedule, so the card shows
  // the upcharge under the name. No-cost colours show nothing.
  const price = getDisplayPrice(data?.price);
  // Swatch assets are not one shape across projects: Corvette ships square paint chips
  // (1:1) and 16:9 interior photos, others ship wide side-profile renders (~3.4:1), and
  // the card's image box is ~2:1. Pick the fit from the image's own aspect rather than
  // hard-coding one — cover (fill, cropping top/bottom) when the swatch is no wider than
  // the box, contain when it is wider, so a side profile never loses its nose and tail.
  // Blanket object-contain left the square chips pillarboxed with bare background.
  const boxRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [fit, setFit] = useState<'cover' | 'contain'>('contain');
  const measureFit = useCallback(() => {
    const img = imgRef.current;
    const box = boxRef.current;
    if (!img?.naturalWidth || !img.naturalHeight || !box?.clientHeight) return;
    setFit(img.naturalWidth / img.naturalHeight <= box.clientWidth / box.clientHeight ? 'cover' : 'contain');
  }, []);
  // Covers cached images, which are already complete before onLoad can fire.
  useLayoutEffect(() => { measureFit(); }, [measureFit, image]);
  return (
    <div
      className={cn('group cursor-pointer relative overflow-hidden rounded-[var(--brand-card-radius-sm)] transition-all')}
      style={{ border: selected ? '1.092px solid var(--brand-accent)' : isDarkMode ? '1.092px solid rgba(255,255,255,0.1)' : '1.092px solid #e6e6e6' }}
    >
      {/* image area (image if provided, else a solid hex swatch, else neutral) */}
      <div ref={boxRef} className="h-[83px] md:h-[91px] overflow-hidden bg-gray-100 flex items-center justify-center">
        {image
          ? <img ref={imgRef} src={image} alt={label} onLoad={measureFit} className={cn('w-full h-full', fit === 'cover' ? 'object-cover' : 'object-contain')} />
          : hex
            ? <div className="w-full h-full" style={{ backgroundColor: hex }} />
            : <div className={cn('w-full h-full', isDarkMode ? 'bg-white/10' : 'bg-gray-200')} />}
      </div>
      {/* selected check */}
      {selected && (
        <div className="absolute top-[6px] right-[8px] w-[14px] h-[14px] bg-[var(--brand-accent)] rounded-full flex items-center justify-center">
          <Check className="w-[9px] h-[9px] text-white stroke-[3]" />
        </div>
      )}
      {/* white label band — name, then price underneath */}
      <div className={cn('px-[7px] flex flex-col justify-center gap-[1px]', isDarkMode ? 'bg-[#111]' : 'bg-white')} style={{ height: '46px' }}>
        <AutoScrollText text={label} className={cn('font-sans text-[10px] md:text-[14px] font-bold leading-[15px]', isDarkMode ? 'text-white/80' : 'text-[#212121]')} />
        <span className={cn('font-sans text-[9px] md:text-[11px] font-medium leading-[13px] whitespace-nowrap', isDarkMode ? 'text-white/40' : 'text-[#666]')}>
          {price > 0 ? formatPrice(price, { sign: true }) : ''}
        </span>
      </div>
    </div>
  );
};

interface AccessoryCardProps { selected?: boolean; id?: string; data: any; isDarkMode: boolean; }
const AccessoryCard: React.FC<AccessoryCardProps> = ({ selected = false, data, isDarkMode }) => {
  const { isRTL } = useRTL();
  const label = isRTL ? (data?.label?.ar ?? data?.label?.en ?? '') : (data?.label?.en ?? '');
  const image = data?.image ? assetSrc(data.image) : '';
  const price = getDisplayPrice(data?.price);
  return (
    <div
      className={cn('group cursor-pointer relative overflow-hidden rounded-[var(--brand-card-radius-sm)] transition-all', isDarkMode ? 'bg-[#111]' : 'bg-white')}
      style={{ border: selected ? '1.092px solid var(--brand-accent)' : isDarkMode ? '1.092px solid rgba(255,255,255,0.1)' : '1.092px solid #e6e6e6' }}
    >
      {/* image area — keep an even 16:9 box so thumbnails aren't stretched */}
      <div className={cn('w-full aspect-[16/9] flex items-center justify-center overflow-hidden', isDarkMode ? 'bg-[#111]' : 'bg-white')}>
        {image
          ? <img src={image} alt={label} className="w-full h-full object-cover" />
          : <div className={cn('w-[60px] h-[60px] rounded-full', isDarkMode ? 'bg-white/10' : 'bg-gray-100')} />}
      </div>
      {/* selected check */}
      {selected && (
        <div className="absolute top-[6px] right-[8px] w-[14px] h-[14px] bg-[var(--brand-accent)] rounded-full flex items-center justify-center">
          <Check className="w-[9px] h-[9px] text-white stroke-[3]" />
        </div>
      )}
      {/* white label band — name, then price underneath */}
      <div
        className={cn('px-[7px] flex flex-col justify-center gap-[1px]', isDarkMode ? 'bg-[#111]' : 'bg-white')}
        style={{ height: '46px', borderTop: isDarkMode ? '0.25px solid rgba(255,255,255,0.05)' : '0.25px solid #eaeaea' }}
      >
        <AutoScrollText text={label} className={cn('font-sans text-[12px] md:text-[14px] font-bold leading-[15px]', isDarkMode ? 'text-white/80' : 'text-[#212121]')} />
        <span className={cn('font-sans text-[9px] md:text-[11px] font-medium leading-[13px] whitespace-nowrap', isDarkMode ? 'text-white/40' : 'text-[#666]')}>
          {price > 0 ? formatPrice(price, { sign: true }) : ''}
        </span>
      </div>
    </div>
  );
};

interface PackageCardProps { selected?: boolean; id?: string; data: any; isDarkMode: boolean; onInfoClick?: (id: string) => void; }
const PackageCard: React.FC<PackageCardProps> = ({ selected = false, id, data, isDarkMode, onInfoClick }) => {
  const { isRTL } = useRTL();
  const label = isRTL ? (data?.label?.ar ?? data?.label?.en ?? '') : (data?.label?.en ?? '');
  const description = isRTL ? (data?.description?.ar ?? data?.description?.en ?? '') : (data?.description?.en ?? '');
  const image = data?.image ? assetSrc(data.image) : '';
  const price = getDisplayPrice(data?.price);
  // The info button opens a sheet built from packageDescription +
  // accessoryContent. Packages that carry neither (e.g. Blackwing's carbon
  // fibre ones) would open an empty sheet, so the button is hidden for them.
  const hasInfo = Boolean(data?.packageDescription || data?.accessoryContent?.length);
  return (
    <div className={cn('group flex border transition-all cursor-pointer rounded-[var(--brand-card-radius-lg)] overflow-hidden', selected ? 'border-[var(--brand-accent)] bg-[var(--brand-accent)]/5' : isDarkMode ? 'border-white/5 bg-[#111111]' : 'border-gray-100 bg-white shadow-sm')}>
      <div className="w-[120px] h-[80px] flex-shrink-0">
        {image ? <img src={image} alt={label} className="w-full h-full object-cover" /> : <div className={cn('w-full h-full flex items-center justify-center', isDarkMode ? 'bg-white/5' : 'bg-gray-100')}><span className={cn('text-[10px]', isDarkMode ? 'text-white/20' : 'text-gray-300')}>PKG</span></div>}
      </div>
      <div className="flex-1 p-4 flex items-center justify-between gap-2 min-w-0">
        <div className="flex flex-col gap-1 min-w-0">
          <AutoScrollText text={label} className={cn('font-sans text-[14px] font-bold leading-[16.5px]', isDarkMode ? 'text-white' : 'text-[#212121]')} />
          {description && <AutoScrollText text={description} className={cn('font-sans text-[12px] font-normal leading-[16.5px]', isDarkMode ? 'text-white/40' : 'text-[#666]')} />}
          <span className={cn('font-sans text-[12px] font-medium leading-[16.5px] whitespace-nowrap', isDarkMode ? 'text-white/40' : 'text-[#666]')}>
            {price > 0 ? formatPrice(price, { sign: true }) : ''}
          </span>
        </div>
        {hasInfo && (
          <button
            onClick={(e) => { e.stopPropagation(); onInfoClick?.(id ?? ''); }}
            className={cn('w-6 h-6 rounded-full border flex items-center justify-center transition-all flex-shrink-0 hover:bg-[var(--brand-accent)] hover:border-[var(--brand-accent)] hover:text-white', isDarkMode ? 'border-white/10 text-white/40' : 'border-gray-200 text-gray-400')}>
            <span className="text-[10px] font-bold">i</span>
          </button>
        )}
      </div>
    </div>
  );
};

interface FeatureCardProps { selected?: boolean; id?: string; progress?: number | null; data: any; isDarkMode: boolean; }
const FeatureCard: React.FC<FeatureCardProps> = ({ selected = false, progress, data, isDarkMode }) => {
  const { isRTL } = useRTL();
  const title = isRTL ? (data?.title?.ar ?? data?.label?.ar ?? data?.title?.en ?? data?.label?.en ?? '') : (data?.title?.en ?? data?.label?.en ?? '');
  const desc = isRTL ? (data?.desc?.ar ?? data?.desc?.en ?? '') : (data?.desc?.en ?? '');
  const image = data?.image ? assetSrc(data.image) : '';
  const { status } = useFeatureStore();
  const isPlaying = selected && status === 'play';
  // Staged progress: never show the previous feature's carried-over value; animate forward
  // only. See useStagedProgress.
  const { shown, transition: barTransition } = useStagedProgress(selected, progress);
  return (
    <div
      className={cn(
        'group relative flex overflow-hidden transition-all cursor-pointer bg-white',
        selected ? 'border-[var(--brand-accent)] border-[1.2px]' : isDarkMode ? 'border-[1.2px] border-white/10 bg-[#111111]' : 'border-[1.2px] border-[#e6e6e6]',
      )}
      style={{ borderRadius: 'var(--brand-card-radius)' }}
    >
      {/* Thumbnail */}
      <div className="w-[135px] h-[119px] flex-shrink-0 overflow-hidden">
        {image
          ? <img src={image} alt={title} className="w-full h-full object-cover" />
          : <div className={cn('w-full h-full', isDarkMode ? 'bg-white/5' : 'bg-gray-100')} />}
      </div>
      {/* Content */}
      <div className="flex-1 flex items-center justify-between gap-3 px-[14px] min-w-0 overflow-hidden">
        <div className="flex flex-col gap-[4px] min-w-0 overflow-hidden">
          <AutoScrollText text={title} className={cn('font-sans text-[14px] font-bold capitalize leading-normal', isDarkMode ? 'text-white' : 'text-black')} />
          {desc && (
            <p className={cn('font-sans text-[12px] font-normal capitalize leading-normal line-clamp-2', isDarkMode ? 'text-white/50' : 'text-black')}>{desc}</p>
          )}
        </div>
        <button className={cn('w-[25px] h-[25px] rounded-full border flex items-center justify-center transition-all flex-shrink-0', selected ? 'border-[var(--brand-accent)] text-[var(--brand-accent)] bg-blue-50' : isDarkMode ? 'border-white/30 text-white hover:bg-white/10' : 'border-[#212121] text-black hover:bg-gray-50')}>
          {isPlaying ? <Pause className="w-[9px] h-[9px] fill-current" /> : <Play className="w-[9px] h-[9px] fill-current" />}
        </button>
      </div>
      {/* Progress bar at bottom */}
      {selected && shown > 0 && (
        <div
          className="absolute bottom-0 left-0 h-[6px] bg-[var(--brand-accent)] rounded-[var(--brand-pill-radius)]"
          style={{ width: `${shown}%`, transition: barTransition }}
        />
      )}
    </div>
  );
};

interface ControlledFeatureGroupProps {
  children: React.ReactNode;
  className?: string;
  videoElementId?: string;
}

type FeatureGroupContextValue = {
  setFeatureState?: React.Dispatch<
    React.SetStateAction<{
      controller?: AbortController;
      startFromId: string;
      playAll?: boolean;
      runKey?: number;
    }>
  >;
  videoElementId?: string;
};

const ControlledFeatureGroup: React.FC<ControlledFeatureGroupProps> = ({
  children,
  className,
  videoElementId,
}) => {
  const [featureState, setFeatureState] = useState<{
    controller?: AbortController;
    startFromId: string;
    playAll?: boolean;
    runKey?: number;
  }>({
    controller: undefined,
    startFromId: '',
  });

  // Reset the shared progress to 0 the instant the active feature changes, so a newly
  // selected feature never briefly shows the previous feature's progress before its own
  // run begins. Fires synchronously with setCurrentFeatureId (same render).
  useEffect(() => {
    return FeatureStore.subscribe(
      (s) => s.currentFeatureId,
      () => { FeatureStore.getState().setProgress(0); },
    );
  }, []);

  useEffect(() => {
    if (!featureState.controller) return;

    let hasStartedSingleFeature = false;
    const singleFeatureId = featureState.playAll === false ? featureState.startFromId : '';

    HandleFeaturesAutoplay(
      featureState.controller.signal,
      videoElementId,
      featureState.startFromId,
      undefined,
      featureState.playAll ?? true,
    );

    const unsubscribeProgress = singleFeatureId
      ? FeatureStore.subscribe(
          (state) => state.progress,
          (progress) => {
            const currentFeatureId = FeatureStore.getState().currentFeatureId;
            if (currentFeatureId === singleFeatureId) {
              hasStartedSingleFeature = true;
            }

            if (hasStartedSingleFeature && currentFeatureId === singleFeatureId && progress >= 99) {
              featureState.controller?.abort();
            }
          },
        )
      : undefined;

    const unsubscribeFeature = singleFeatureId
      ? FeatureStore.subscribe(
          (state) => state.currentFeatureId,
          (currentFeatureId, previousFeatureId) => {
            if (currentFeatureId === singleFeatureId) {
              hasStartedSingleFeature = true;
            }

            if (
              hasStartedSingleFeature &&
              previousFeatureId === singleFeatureId &&
              currentFeatureId !== singleFeatureId
            ) {
              featureState.controller?.abort();
            }
          },
        )
      : undefined;

    return () => {
      unsubscribeProgress?.();
      unsubscribeFeature?.();
      featureState.controller?.abort();
    };
  }, [featureState, videoElementId]);

  return (
    <FeatureGroupContext.Provider value={{ setFeatureState, videoElementId }}>
      <div className={className}>{children}</div>
    </FeatureGroupContext.Provider>
  );
};

interface PriorityFeatureProps {
  featureID: string;
  data: any;
  isDarkMode: boolean;
  isPlayAllActive: boolean;
  setIsPlayAllActive: (active: boolean) => void;
}

const PriorityFeature: React.FC<PriorityFeatureProps> = ({
  featureID,
  data,
  isDarkMode,
  isPlayAllActive,
  setIsPlayAllActive,
}) => {
  const { isRTL } = useRTL();
  const { setFeatureState, videoElementId } =
    useFeatureGroupContext() as FeatureGroupContextValue;
  const { currentFeatureId, status, progress } = useFeatureStore();
  const { view } = useGetConfiguration();
  const { trackEvent } = useLogEvent();
  const featureName = isRTL ? (data?.title?.ar ?? data?.label?.ar ?? data?.title?.en ?? data?.label?.en ?? '') : (data?.title?.en ?? data?.label?.en ?? '');
  const selected = currentFeatureId === featureID;
  // When a new play is triggered, suppress the stale progress until the SDK starts fresh progress
  const [isResetting, setIsResetting] = React.useState(false);

  React.useEffect(() => {
    if (isResetting && selected && progress != null) {
      setIsResetting(false);
    }
  }, [isResetting, selected, progress]);

  const playSingleFeature = () => {
    trackEvent(AnalyticsIds.featureInteract, {
      state: true,
      parent: view.type,
      primaryTypeName: featureName,
      secondaryTypeName: 'manual',
    });
    setIsPlayAllActive(false);
    setIsResetting(true);
    setFeatureState?.({
      controller: new AbortController(),
      startFromId: featureID,
      playAll: false,
      runKey: Date.now(),
    });
  };

  return (
    <div
      id={featureID}
      // style={{ scrollMarginTop: '1px' }}
      onPointerUp={(event) => {
        event.stopPropagation();

        if (isPlayAllActive || !selected) {
          playSingleFeature();
          return;
        }

        if (status === 'play') {
          trackEvent(AnalyticsIds.featureAutoplayInteract, {
            state: false,
            primaryTypeName: featureName,
            secondaryTypeName: 'pause',
            tertiaryTypeName: String(Math.round(progress)),
          });
          HandleFeaturePause('pause', videoElementId);
        } else if (status === 'pause') {
          trackEvent(AnalyticsIds.featureAutoplayInteract, {
            state: true,
            primaryTypeName: featureName,
            secondaryTypeName: 'play',
            tertiaryTypeName: String(Math.round(progress)),
          });
          HandleFeaturePause('resume', videoElementId);
        } else {
          // Feature completed or idle — restart from beginning
          playSingleFeature();
        }
      }}
    >
      <FeatureCard
        selected={selected}
        id={featureID}
        progress={selected && !isResetting ? progress : 0}
        data={data}
        isDarkMode={isDarkMode}
      />
    </div>
  );
};

// Renders inside FeatureGroup so it has access to FeatureGroupContext
interface PlayAllSectionProps {
  featureIDs: string[];
  modelData: any;
  isDarkMode: boolean;
  autoPlayOnMount?: boolean;
  onAutoPlayStarted?: () => void;
}
const PlayAllSection: React.FC<PlayAllSectionProps> = ({
  featureIDs,
  modelData,
  isDarkMode,
  autoPlayOnMount = false,
  onAutoPlayStarted,
}) => {
  const { t } = useTranslation();
  const { isRTL } = useRTL();
  const { setFeatureState, videoElementId } =
    useFeatureGroupContext() as FeatureGroupContextValue;
  const { currentFeatureId, status, progress } = useFeatureStore();
  const { view } = useGetConfiguration();
  const { trackEvent } = useLogEvent();
  const [isPlayAllActive, setIsPlayAllActive] = useState(false);
  const didAutoPlayRef = useRef(false);

  const getFeatureName = (id: string | null) => {
    const data = id ? modelData?.features?.[id] : undefined;
    return isRTL
      ? (data?.title?.ar ?? data?.label?.ar ?? data?.title?.en ?? data?.label?.en ?? '')
      : (data?.title?.en ?? data?.label?.en ?? '');
  };

  // Play All button state — only reflects play-all mode, not individual feature play
  const isPlaying = isPlayAllActive && currentFeatureId !== null && status === 'play';
  const isPaused = isPlayAllActive && currentFeatureId !== null && status === 'pause';

  const handlePlayAll = () => {
    if (isPlaying) {
      trackEvent(AnalyticsIds.featureAutoplayInteract, {
        state: false,
        primaryTypeName: getFeatureName(currentFeatureId),
        secondaryTypeName: 'pause',
        tertiaryTypeName: String(Math.round(progress)),
      });
      HandleFeaturePause('pause', videoElementId);
    } else if (isPaused) {
      trackEvent(AnalyticsIds.featureAutoplayInteract, {
        state: true,
        primaryTypeName: getFeatureName(currentFeatureId),
        secondaryTypeName: 'play',
        tertiaryTypeName: String(Math.round(progress)),
      });
      HandleFeaturePause('resume', videoElementId);
    } else {
      trackEvent(AnalyticsIds.featureAutoplayInteract, {
        state: true,
        primaryTypeName: getFeatureName(featureIDs[0] ?? null),
        secondaryTypeName: 'play',
        tertiaryTypeName: '0',
      });
      setIsPlayAllActive(true);
      setFeatureState?.({ controller: new AbortController(), startFromId: '', playAll: true, runKey: Date.now() });
    }
  };

  // Feature auto-advancing during Play All has no click behind it — the SDK's
  // HandleFeaturesAutoplay drives FeatureStore directly — so this is watched
  // rather than hung off an onClick like the rest of the harness.
  const prevAutoplayFeatureIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (isPlayAllActive && currentFeatureId && currentFeatureId !== prevAutoplayFeatureIdRef.current) {
      trackEvent(AnalyticsIds.featureInteract, {
        state: true,
        parent: view.type,
        primaryTypeName: getFeatureName(currentFeatureId),
        secondaryTypeName: 'autoplay',
      });
    }
    prevAutoplayFeatureIdRef.current = currentFeatureId;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFeatureId, isPlayAllActive]);

  const completedFeatureIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (
      currentFeatureId &&
      progress >= 99 &&
      completedFeatureIdRef.current !== currentFeatureId
    ) {
      completedFeatureIdRef.current = currentFeatureId;
      trackEvent(AnalyticsIds.featureAutoplayInteract, {
        primaryTypeName: getFeatureName(currentFeatureId),
        secondaryTypeName: 'complete',
        tertiaryTypeName: String(Math.round(progress)),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress, currentFeatureId]);

  useEffect(() => {
    if (!autoPlayOnMount || didAutoPlayRef.current || featureIDs.length === 0) {
      return;
    }

    didAutoPlayRef.current = true;
    setIsPlayAllActive(true);
    setFeatureState?.({
      controller: new AbortController(),
      startFromId: '',
      playAll: true,
      runKey: Date.now(),
    });
    onAutoPlayStarted?.();
  }, [autoPlayOnMount, featureIDs.length, onAutoPlayStarted, setFeatureState]);

  return (
    <>
      <div className="flex items-center justify-between mb-[16px]">
        <h3 className={cn('font-sans text-[16px] font-bold leading-[20px] tracking-[0.35px] uppercase', isDarkMode ? 'text-white' : 'text-[#212121]')}>
          {t('staticTranslations.previewFeature', 'Preview Feature')}
        </h3>
        <button onClick={handlePlayAll} className="flex items-center gap-[6px] group">
          <span className={cn('font-sans text-[16px] font-bold leading-[20px] tracking-[0.35px] uppercase transition-colors', isDarkMode ? 'text-white group-hover:text-white/80' : 'text-[#212121] group-hover:text-black')}>
            {isPlaying ? t('staticTranslations.configPanel.pause', 'Pause') : t('staticTranslations.configPanel.playAll', 'Play All')}
          </span>
          {!isPlaying
            ? <img src={playAllIcon} alt="Play All" className={cn('w-[25px] h-[28px]', !isDarkMode && 'brightness-0')} />
            : <div className={cn('w-[25px] h-[25px] rounded-full border flex items-center justify-center transition-all', isDarkMode ? 'border-white/20 text-white' : 'border-black text-black')}>
                <Pause className="w-3 h-3 fill-current" />
              </div>
          }
        </button>
      </div>
      <>
        {featureIDs.map((id) => {
          const data = modelData?.features?.[id];
          return (
            <PriorityFeature
              featureID={id}
              key={id}
              data={data}
              isDarkMode={isDarkMode}
              isPlayAllActive={isPlayAllActive}
              setIsPlayAllActive={setIsPlayAllActive}
            />
          );
        })}
      </>
    </>
  );
};

// Accessory ids for a category (from graphics metadata so defaults are included),
// filtered by the current variant and optionally by view. Category comes from
// ui-metadata. Used to render Corvette's exterior groups + option packages.
const getAccessoryIdsByCategory = (
  modelId: string,
  variantID: string | undefined,
  category: string,
  view?: 'interior' | 'exterior',
  exteriorColorID?: string,
  interiorColorID?: string,
): string[] => {
  const gm: any = (metadata as any)?.models?.[modelId]?.accessories ?? {};
  const um: any = (uimetadata as any)?.models?.[modelId]?.accessories ?? {};
  return Object.keys(gm).filter(
    (id) =>
      gm[id]?.relevantVariants?.includes(variantID) &&
      gm[id]?.display !== false && // hidden base-state defaults never render as cards
      (view ? gm[id]?.view === view : true) &&
      // Hide accessories not available for the current exterior colour (e.g. GS
      // heritage graphics restricted from certain body colours). The SDK's colour
      // conflict system also removes an active one when such a colour is applied.
      !(exteriorColorID && gm[id]?.notAvailableForColor?.exterior?.includes(exteriorColorID)) &&
      // Same, for interior colour (e.g. Tahoe LT's seat accessories, which 1.0
      // only ever showed on the non-default interior via a forColor allowlist).
      // Without this the card renders but clicking it hangs forever: the SDK's
      // click handler awaits a "pick a compatible colour" prompt this app never
      // implemented (PauseExecutionForOptions has no dialog wired to it).
      !(interiorColorID && gm[id]?.notAvailableForColor?.interior?.includes(interiorColorID)) &&
      um[id]?.category?.en === category,
  );
};

// Id of the interaction labelled `label` (e.g. 'Doors') for the current variant.
// Some models keep one such interaction per trim group (Lyriq: 2025 Luxury vs
// 2025 Sport / 2026 Lyriq-V), so the first label match can belong to another
// trim - closing that one is a no-op and the current trim's doors stay open.
// Falls back to the first match when none lists the variant.
const findVariantInteractionId = (modelId: string, variantID: string | undefined, label: string): string | undefined => {
  const ui: any = (uimetadata as any)?.models?.[modelId]?.interactions ?? {};
  const gfx: any = (metadata as any)?.models?.[modelId]?.interactions ?? {};
  const ids = Object.keys(ui).filter((id) => ui[id]?.label?.en === label);
  return ids.find((id) => gfx[id]?.relevantVariants?.includes(variantID)) ?? ids[0];
};

// Exterior accessory groups for Corvette-style projects (rendered as sections).
const CORVETTE_EXTERIOR_CATEGORIES = ['Caliper', 'Roof', 'Engine Cover', 'Exhaust Tip', 'Spoiler', 'Alloy Wheels', 'Graphics'];

// Accessory categories rendered as their own sections, per tab. A project opts
// in by setting `models.<id>.accessoryCategories` in its ui-metadata (Tahoe
// 2365 does — it has Alloy Wheels and Roof groups); Corvette keeps the
// hardcoded list above, and everything else falls back to the single
// Roof / Seats / Accessories groups.
const categoriesForTab = (modelData: any, tab: 'exterior' | 'interior' | 'options'): string[] | null => {
  const fromMeta = modelData?.accessoryCategories?.[tab];
  if (fromMeta) return fromMeta;
  if (tab === 'exterior' && modelData?.interiorConfig) return CORVETTE_EXTERIOR_CATEGORIES;
  return null;
};

// Alloy wheels are separate SKU assets, not interactions: selecting one swaps the
// 'alloy' asset's SKU (SwapSku) rather than applying an accessory interaction.
const CORVETTE_ALLOY_SKU: Record<string, string> = {
  accAlloyQEB: '6789', accAlloyQ99: '6690', accAlloyQ9A: '6742', accAlloyQ9I: '6788', accAlloyQ9O: '6792', accAlloyQE6: '7007',
  gsAlloyROU: '6712', gsAlloyROX: '6797', gsAlloySOM: '6746', gsAlloySON: '6791', gsAlloyROY: '6793', gsAlloyROZ: '6794', gsAlloySTZ: '6691',
};

// ── Interior cascade (steering wheel → seat → seat belt → stitch) ───────────
// Driven entirely by `modelData.interiorConfig` (present only for linkIds that
// define it, e.g. Corvette 2387). Renders nothing when absent, so previously
// added linkIds are unaffected. Selecting a seat (GT2/Sport) or changing the
// interior color resets the seat-belt & stitch to that set's defaults.
const InteriorConfigurator: React.FC<{ interiorColorID?: string; modelData: any; isDarkMode: boolean }> = ({ interiorColorID, modelData, isDarkMode }) => {
  const { t } = useTranslation();
  const { accessoriesIDs } = useAccessoryStore();
  const [open, setOpen] = useState<Record<string, boolean>>({ seat: true, seatBelt: true, stitch: true });
  const toggle = (k: string) => setOpen((p) => ({ ...p, [k]: !p[k] }));

  const cfg = interiorColorID ? modelData?.interiorConfig?.[interiorColorID] : undefined;
  // Some variants (e.g. Stingray Coupe-1LT) have no GT2/Sport seat toggle — the
  // interior color loads the seat, and belt/stitch live directly on the cfg.
  const hasSeats = !!(cfg && cfg.seats && Object.keys(cfg.seats).length);
  const seatIds: string[] = hasSeats ? Object.keys(cfg.seats) : [];
  const activeSeatId: string | undefined = hasSeats
    ? (seatIds.find((id) => accessoriesIDs.includes(id)) ?? cfg?.defaultSeatId)
    : undefined;
  const seat = activeSeatId ? cfg.seats[activeSeatId] : undefined;
  // Belt/stitch config comes from the active seat when seats exist, else from cfg.
  const beltCfg = seat ? seat.seatBelt : cfg?.seatBelt;
  const stitchCfg = seat ? seat.stitch : cfg?.stitch;

  const prevColorRef = useRef<string | undefined>(undefined);
  const prevSeatRef = useRef<string | undefined>(undefined);

  const applyOne = (id?: string) => (id ? AddMultipleAccessoriesWithLoading([id], false) : Promise.resolve());
  const applySeq = async (ids: (string | undefined)[]) => {
    // Safety net: AddMultipleAccessoriesWithLoading keeps the accessory-loading
    // overlay up until every applied interaction is acked by the graphics engine.
    // If one never acks, force-clear it after a timeout so the panel stays usable.
    const guard = setTimeout(() => { try { LoaderState.getState().setAccessoryLoading(false); } catch { /* noop */ } }, 12000);
    try { for (const id of ids) await applyOne(id); } finally { clearTimeout(guard); }
  };

  // Interior color changed → apply steering + default seat + default belt + default stitch.
  // If the new color has no stitch, explicitly remove any stitch still applied (the
  // single-select group won't auto-deselect it since no new stitch is applied).
  useEffect(() => {
    if (!cfg || !interiorColorID || prevColorRef.current === interiorColorID) return;
    prevColorRef.current = interiorColorID;
    const defSeatId = hasSeats ? cfg.defaultSeatId : undefined;
    const defSeat = defSeatId ? cfg.seats[defSeatId] : undefined;
    prevSeatRef.current = defSeatId;
    const stitchCfgNew = defSeat ? defSeat.stitch : cfg.stitch;
    const beltCfgNew = defSeat ? defSeat.seatBelt : cfg.seatBelt;
    const belt = beltCfgNew?.default;
    const stitch = stitchCfgNew?.default;
    // Remove any belt/stitch still applied when the new color offers none of them
    // (the single-select group won't auto-deselect without a replacement).
    // Seatless (Mulan) colours carry their own seat in the colour asset, so strip any
    // seat accessory left over from a Napa (GT2/Sport) selection — otherwise the old seat
    // overlaps the Mulan one. Also drop the group's isDefault seat (seat_intJetBlack_gt2):
    // removing a non-default seat makes the SDK re-apply that default (it ignores the
    // current variant), which would leave the Jet Black GT2 seat sitting on the Mulan interior.
    const staleSeats = !hasSeats ? accessoriesIDs.filter((id) => id.startsWith('seat_')) : [];
    const stale = [
      ...(staleSeats.length ? [...staleSeats, 'seat_intJetBlack_gt2'] : []),
      ...(!stitchCfgNew ? accessoriesIDs.filter((id) => id.startsWith('stitch_')) : []),
      ...(!beltCfgNew ? accessoriesIDs.filter((id) => id.startsWith('seatbelt_')) : []),
    ];
    (async () => {
      if (stale.length) {
        const { isVariantLoading } = LoaderState.getState();
        await RemoveMultipleAccessoriesWithLoading(stale, 'accessory', isVariantLoading);
      }
      await applySeq([cfg.steeringWheelId, defSeatId, belt, stitch]);
    })();
  }, [interiorColorID, cfg]);

  // Seat changed → reset belt & stitch to that seat's defaults when current is not in the seat's set
  useEffect(() => {
    if (!cfg || !activeSeatId || !seat || prevSeatRef.current === activeSeatId) return;
    prevSeatRef.current = activeSeatId;
    const belt = seat.seatBelt?.options?.find((id: string) => accessoriesIDs.includes(id));
    const stitch = seat.stitch?.options?.find((id: string) => accessoriesIDs.includes(id));
    applySeq([belt ? undefined : seat.seatBelt?.default, stitch ? undefined : seat.stitch?.default]);
  }, [activeSeatId, cfg, seat, accessoriesIDs]);

  // Defaults (seat / seat belt / stitch) are hidden from the UI. When the user
  // deselects the applied non-default, its group becomes empty — re-apply the
  // group's default so the car never sits with nothing on that part. Debounced so
  // it only inspects the SETTLED state (never mid seat/color switch), which would
  // otherwise race the other applies and hang the accessory loader.
  useEffect(() => {
    if (!cfg || !interiorColorID || prevColorRef.current !== interiorColorID) return;
    const timer = setTimeout(() => {
      const { isAccessoryLoading, isVariantLoading } = LoaderState.getState();
      if (isAccessoryLoading || isVariantLoading) return; // still transitioning
      const groups: Array<{ ids: string[]; def?: string }> = [];
      if (hasSeats) groups.push({ ids: seatIds, def: cfg.defaultSeatId });
      if (beltCfg?.options?.length) groups.push({ ids: beltCfg.options, def: beltCfg.default });
      if (stitchCfg?.options?.length) groups.push({ ids: stitchCfg.options, def: stitchCfg.default });
      const missing = groups
        .filter((g) => g.def && !g.ids.some((id: string) => accessoriesIDs.includes(id)))
        .map((g) => g.def);
      if (missing.length) applySeq(missing);
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessoriesIDs, cfg, interiorColorID]);

  if (!cfg) return null;

  // Hide the default option in each group; show only the alternatives.
  const seatOptions = seatIds.filter((id) => id !== cfg.defaultSeatId);
  const beltOptions: string[] = (beltCfg?.options ?? []).filter((id: string) => id !== beltCfg?.default);
  const stitchOptions: string[] = (stitchCfg?.options ?? []).filter((id: string) => id !== stitchCfg?.default);

  const cardGroup = (ids: string[]) => (
    <AccessoryGroup className="grid grid-cols-2 gap-x-[17px] gap-y-[14px] md:gap-x-[19px] md:gap-y-[16px]">
      {ids.map((id) => (
        <Accessory key={id} accId={id}>
          <AccessoryCard data={modelData?.accessories?.[id]} isDarkMode={isDarkMode} />
        </Accessory>
      ))}
    </AccessoryGroup>
  );

  return (
    <>
      {hasSeats && seatOptions.length ? (
        <AccordionItem title={`${t('staticTranslations.configPanel.seat', 'Seat')} (${seatOptions.length})`} isOpen={open.seat} onToggle={() => toggle('seat')} isDarkMode={isDarkMode} parent="INTERIOR">
          {cardGroup(seatOptions)}
        </AccordionItem>
      ) : null}
      {beltOptions.length ? (
        <AccordionItem title={`${t('staticTranslations.configPanel.seatBelt', 'Seat Belt')} (${beltOptions.length})`} isOpen={open.seatBelt} onToggle={() => toggle('seatBelt')} isDarkMode={isDarkMode} parent="INTERIOR">
          {cardGroup(beltOptions)}
        </AccordionItem>
      ) : null}
      {stitchOptions.length ? (
        <AccordionItem title={`${t('staticTranslations.configPanel.stitch', 'Stitch')} (${stitchOptions.length})`} isOpen={open.stitch} onToggle={() => toggle('stitch')} isDarkMode={isDarkMode} parent="INTERIOR">
          {cardGroup(stitchOptions)}
        </AccordionItem>
      ) : null}
    </>
  );
};

// Alloy wheels: each option is a distinct SKU on the shared 'alloy' asset, so
// selecting one SWAPS that asset's SKU (SwapSku) instead of applying an accessory
// interaction (which would pile a second wheel). The default is the variant's
// 'alloy' defaultSku; selection is local UI state.
const AlloyConfigurator: React.FC<{ ids: string[]; modelData: any; variantID: string; isDarkMode: boolean }> = ({ ids, modelData, variantID, isDarkMode }) => {
  // defaultSkus live in the graphics metadata (not the UI modelData), so read the default
  // 'alloy' sku from there — otherwise it's undefined and deselect can't revert the wheel.
  const defaultSku: string | undefined = (metadata as any)?.models?.[uimetadata.config.model]?.variants?.[variantID]?.defaultSkus?.find((s: any) => s.id === 'alloy')?.sku;
  // The default alloy is the variant's defaultSku. When its card is listed (Stingray's
  // QEB) it starts selected; when it's hidden (display:false), select NONE by default
  // rather than defaulting to the first visible (wrong) alloy.
  const defaultId = ids.find((id) => CORVETTE_ALLOY_SKU[id] === defaultSku) ?? '';
  const [selected, setSelected] = useState<string>(defaultId);
  useEffect(() => { setSelected(defaultId); }, [variantID, defaultId]);
  const onSelect = async (id: string) => {
    const sku = CORVETTE_ALLOY_SKU[id];
    if (!sku) return;
    // The default wheel is already on the car - clicking it again does nothing.
    if (id === selected && id === defaultId) return;
    // Clicking the selected alloy again deselects it -> revert to the default alloy.
    const deselect = id === selected;
    const targetSku = deselect ? defaultSku : sku;
    if (deselect && !defaultSku) { setSelected(''); return; }
    setSelected(deselect ? defaultId : id);
    // Show the loading overlay (same one interior/exterior colour changes use, via
    // isColorLoading) while the alloy asset swaps — SwapSku otherwise gives no feedback.
    // Label it "Loading". Guard clears the overlay if the swap never acks.
    setViewportLoaderText('Loading');
    LoaderState.getState().setColorLoading(true);
    const guard = setTimeout(() => { try { LoaderState.getState().setColorLoading(false); } finally { setViewportLoaderText(null); } }, 12000);
    try {
      await SwapSku({ id: 'alloy', sku: targetSku as string, projectId: '59' } as any);
      // Alloys are applied via SwapSku (asset swap), not the accessory pipeline, so no
      // camera/view is triggered. Mark it the active accessory to fire its CAMERA_ALLOY
      // and bring up the Free Roam view (SDK AccessoryStore subscription).
      if (!deselect) await SetActiveAccessory(id);
    } finally {
      clearTimeout(guard);
      LoaderState.getState().setColorLoading(false);
      setViewportLoaderText(null);
    }
  };
  return (
    <AccessoryGroup className="grid grid-cols-2 gap-x-[17px] gap-y-[14px] md:gap-x-[19px] md:gap-y-[16px]">
      {ids.map((id) => (
        <div key={id} onClick={() => onSelect(id)} className="cursor-pointer">
          <AccessoryCard data={modelData?.accessories?.[id]} selected={selected === id} isDarkMode={isDarkMode} />
        </div>
      ))}
    </AccessoryGroup>
  );
};

export const ConfigPanel: React.FC<ConfigPanelProps> = ({ activeTab, isPreviewMode, setIsPreviewMode, isDarkMode, onPackageInfoClick }) => {
  const { t } = useTranslation();
  const { isRTL } = useRTL();
  const { trackEvent } = useLogEvent();
  const { variantID, exteriorColorID, interiorColorID, accessoriesIDs, packageIDs, view } = useGetConfiguration();
  const modelId = uimetadata.config.model;
  const modelData = (uimetadata.models as any)[modelId];

  // "Previous value" bookkeeping for price-delta segmentation — updated only
  // from the SDK's onClick callbacks below, so a programmatic config change
  // (e.g. a default re-applying on variant switch) never counts as a click.
  const prevExteriorColorRef = useRef(exteriorColorID);
  const prevInteriorColorRef = useRef(interiorColorID);

  const handleColorClick = (
    type: 'exterior-color' | 'interior-color',
    colorId: string
  ) => {
    const isExterior = type === 'exterior-color';
    const prevRef = isExterior ? prevExteriorColorRef : prevInteriorColorRef;
    const prevId = prevRef.current;
    const colorMap = isExterior ? modelData?.colors?.exterior : modelData?.colors?.interior;
    const newData = colorMap?.[colorId];
    const prevData = prevId ? colorMap?.[prevId] : undefined;
    const priceDelta = String(getPrice(newData?.price) - getPrice(prevData?.price));
    if (isExterior) {
      trackEvent(AnalyticsIds.colorInteract, {
        parent: view.type,
        primaryTypeName: newData?.label?.en || colorId,
        secondaryTypeName: priceDelta,
      });
    } else {
      trackEvent(AnalyticsIds.interiorInteract, {
        parent: 'int',
        primaryTypeName: newData?.label?.en || colorId,
        secondaryTypeName: priceDelta,
      });
    }
    prevRef.current = colorId;
  };

  const prevWheelRef = useRef<string | null>(null);
  const handleAccessoryClick = (_type: 'accessory', accId: string) => {
    const accData = modelData?.accessories?.[accId];
    if (accData?.category?.en !== 'Alloy Wheels') return;
    // The SDK's onClick fires synchronously, before the (async) add/remove
    // resolves, so `accessoriesIDs` here is still the pre-click snapshot:
    // already-selected means this click is deselecting it — only track a
    // genuine "user selects a wheel" per the sheet, i.e. it was NOT selected.
    const wasSelected = accessoriesIDs.includes(accId);
    if (wasSelected) return;
    const prevId = prevWheelRef.current;
    const prevData = prevId ? modelData?.accessories?.[prevId] : undefined;
    trackEvent(AnalyticsIds.wheelInteract, {
      parent: view.type,
      primaryTypeName: accData?.label?.en || accId,
      secondaryTypeName: String(getPrice(accData?.price) - getPrice(prevData?.price)),
      tertiaryTypeName: view.type,
    });
    prevWheelRef.current = accId;
  };

  const { variantIDs: allVariantIDs } = useGetActiveVariantIDs();
  // Presentation-selected journey (Stingray / GS X) — the configurator only shows
  // that journey's variants.
  const selectedJourney = useSelectedJourney();
  // Variants marked `display: false` in ui-metadata (e.g. Coupe 1LT) stay in the
  // SDK's list but must not appear as selectable model cards. Also gate by journey
  // so only Stingray or only GSX variants show (id prefix `gsx_` = GSX).
  const variantIDs = allVariantIDs.filter(
    (id) => modelData?.variants?.[id]?.display !== false && journeyOf(id) === selectedJourney,
  );
  const { colorsIDs } = useGetActiveColorIDs(variantID);
  const { accessoryIDs } = useGetActiveAccessoryIDs(variantID, 'exterior');
  // The SDK hook filters by view, so a genuinely interior-view accessory (e.g.
  // Terrain/Sierra's floor/front liners) never appears via the 'exterior' call
  // above. Fetched separately and merged only into the generic Options &
  // Accessories bucket below — Roof/Seats stay 'exterior'-only as before.
  const { accessoryIDs: interiorAccessoryIDs } = useGetActiveAccessoryIDs(variantID, 'interior');
  const { packageIDs: activePackageIDs } = useGetActivePackageIDs(variantID);
  const { packageIDs: selectedPackageIDs } = usePackageStore();
  const featureViewType = activeTab === 'INTERIOR' ? 'interior' : activeTab === 'EXTERIOR' ? 'exterior' : view.type;
  const { featureIDs: allFeatureIDs } = useGetActiveFeatureIDs(variantID, featureViewType);
  // Hide the "Performance Exhaust" showcase feature when a Quad center-exit exhaust
  // option is selected — the quad tips replace the performance-exhaust presentation.
  const quadExhaustSelected = (accessoriesIDs ?? []).some(
    (a) => a === 'accQuadExhaustBlack' || a === 'accQuadExhaustBright',
  );
  const featureIDs = quadExhaustSelected
    ? allFeatureIDs.filter((id) => id !== 'featExhaust')
    : allFeatureIDs;

  const [openSections, setOpenSections] = useState<Record<string, boolean>>({ colors: true, accessories: true, interior: true, packages: true });
  const [hasAutoPlayedPreviewFeatures, setHasAutoPlayedPreviewFeatures] = useState(false);
  const [selectedDrivetrain, setSelectedDrivetrain] = useState<string>('');
  // Body style is derived from the variant id/name ('conv' => Convertible).
  const bodyStyleOf = (id: string): 'Coupe' | 'Convertible' => {
    const name: string = (modelData?.variants?.[id]?.name?.en ?? id).toLowerCase();
    return id.toLowerCase().includes('conv') || name.includes('convertible') ? 'Convertible' : 'Coupe';
  };
  // Body Style filter (CONFIG tab): 'Coupe' | 'Convertible' (no "All"). Defaults to the
  // current variant's body style so the selected trim stays visible in the model list.
  const [bodyStyleFilter, setBodyStyleFilter] = useState<string>(() => bodyStyleOf(variantID));
  // Keep selectedDrivetrain in sync when variantID changes externally
  useEffect(() => {
    const dt: string = modelData?.variants?.[variantID]?.drivetrain ?? modelData?.variants?.[variantID]?.description?.en ?? '';
    if (dt) setSelectedDrivetrain(dt);
  }, [variantID, modelData]);
  // Close the doors when the exterior/interior color changes, so the door isn't left
  // floating over the newly-painted body. Skip the initial mount. HandleInteraction is
  // state-aware — it's a no-op if the doors are already closed. The doors interaction id
  // is an opaque, per-model string (CT5 uses 'ihmz2ch7lru2', Corvette uses 'intrDoors'),
  // so it's derived from the current variant's interaction labelled "Doors" rather than
  // hardcoded - hardcoding a single model's id here previously left every other model's doors open.
  const didMountColorRef = useRef(false);
  useEffect(() => {
    if (!didMountColorRef.current) { didMountColorRef.current = true; return; }
    const doorsInteractionId = findVariantInteractionId(modelId, variantID, 'Doors');
    const bootInteractionId = findVariantInteractionId(modelId, variantID, 'Boot');
    if (doorsInteractionId) void HandleInteraction(doorsInteractionId, false, { snapToLastFrame: true });
    if (bootInteractionId) void HandleInteraction(bootInteractionId, false, { snapToLastFrame: true });
  }, [exteriorColorID, interiorColorID, modelData]);
  // Close the doors and boot when any package gets applied (Technology Package,
  // Platinum Package, etc.). Packages trigger accessory changes that can leave
  // doors/boot floating over the newly-changed model. Keyed off
  // selectedPackageIDs (usePackageStore) rather than configuration's packageIDs
  // — AddPackage/RemovePackage only ever update PackageStore, so
  // configuration's packageIDs never reflects live apply/remove and that
  // dependency would never fire. prevPackageIDsRef starts equal to
  // selectedPackageIDs, so this is naturally a no-op on mount (nothing counts
  // as "newly applied" yet).
  const prevPackageIDsRef = useRef<string[]>(selectedPackageIDs);
  useEffect(() => {
    const prevPackageIDs = prevPackageIDsRef.current;
    prevPackageIDsRef.current = selectedPackageIDs;
    const newlyApplied = selectedPackageIDs.filter((id) => !prevPackageIDs.includes(id));
    if (newlyApplied.length === 0) return;
    const doorsInteractionId = findVariantInteractionId(modelId, variantID, 'Doors');
    const bootInteractionId = findVariantInteractionId(modelId, variantID, 'Boot');
    if (doorsInteractionId) void HandleInteraction(doorsInteractionId, false, { snapToLastFrame: true });
    if (bootInteractionId) void HandleInteraction(bootInteractionId, false, { snapToLastFrame: true });
  }, [selectedPackageIDs, modelData]);
  // Close the doors whenever ANY accessory is newly applied (calipers, roof, exhaust,
  // graphics, spoiler, badge, interior trim, and — via the interior cascade — a seat/belt/
  // stitch from an interior-colour change), so the applied change reads on a closed car.
  // Fires only on apply (a newly-added accessory id), not on removal. Interior/exterior
  // colour changes are additionally covered by the colour-change effect above.
  // snapToLastFrame => doors close instantly with no animation.
  const prevAccIdsRef = useRef<string[]>(accessoriesIDs ?? []);
  useEffect(() => {
    const prev = prevAccIdsRef.current;
    prevAccIdsRef.current = accessoriesIDs ?? [];
    const newlyApplied = (accessoriesIDs ?? []).filter((id) => !prev.includes(id));
    if (newlyApplied.length === 0) return;
    const doorsInteractionId = findVariantInteractionId(modelId, variantID, 'Doors');
    if (doorsInteractionId) void HandleInteraction(doorsInteractionId, false, { snapToLastFrame: true });
  }, [accessoriesIDs, modelData]);
  const toggleSection = (section: string) => setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  const showPreviewButton = ['EXTERIOR', 'INTERIOR', 'OPTIONS'].includes(activeTab);
  // Block the panel while a color is loading so the user can't spam color swaps
  // (which reload assets) faster than the engine can apply them.
  const isColorLoading = LoaderState((s) => s.isColorLoading);

  const filterOptions: Array<{ id: string; label: { en: string; ar: string }; disabled: boolean; type: string; key: string; options: Array<{ id: string; label: { en: string; ar: string }; value: string; checked: boolean }> }> = (uimetadata as any).filterOptions ?? [];
  // Pre-select whatever the metadata marks as `checked` (Tahoe 2365 ships a
  // Model Year filter with 2026 checked). Projects whose options are all
  // unchecked start with no constraint, exactly as before. A variant restored from
  // a shared URL may fall outside the pre-checked values (e.g. a 2025 trim under
  // the 2026 default), so its own value is added to keep it visible in the list.
  const [activeFilters, setActiveFilters] = useState<Record<string, Set<string>>>(() =>
    Object.fromEntries(
      filterOptions
        .filter((f) => !f.disabled)
        .map((f) => {
          const set = new Set(f.options.filter((o) => o.checked).map((o) => o.value));
          const current = (modelData?.variants?.[variantID] as any)?.[f.key];
          if (set.size > 0 && current !== undefined) set.add(String(current));
          return [f.id, set];
        })
        .filter(([, set]) => (set as Set<string>).size > 0),
    ),
  );
  // ConfigPanel mounts before the SDK applies a shared URL's configuration, so the
  // initialiser above only sees the default variant. Whenever the selected variant
  // changes from outside the list (URL restore, journey switch), widen any filter
  // that would hide it. Toggling a chip doesn't re-run this, so it never fights the user.
  useEffect(() => {
    const variant = modelData?.variants?.[variantID] as any;
    if (!variant) return;
    setActiveFilters((prev) => {
      let next = prev;
      for (const f of filterOptions) {
        if (f.disabled) continue;
        const selected = prev[f.id];
        const value = variant[f.key];
        if (!selected || selected.size === 0 || value === undefined || selected.has(String(value))) continue;
        next = { ...next, [f.id]: new Set([...selected, String(value)]) };
      }
      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variantID, modelData]);

  const toggleFilter = (filterId: string, value: string) => {
    setActiveFilters((prev) => {
      const current = new Set(prev[filterId] ?? []);
      if (current.has(value)) current.delete(value); else current.add(value);
      return { ...prev, [filterId]: new Set(current) };
    });
  };

  /** True when a variant satisfies every filter that has a selection.
   *  A filter matches on the variant field named by its `key` (e.g. modelyear). */
  const matchesActiveFilters = (id: string): boolean =>
    filterOptions.every((f) => {
      if (f.disabled) return true;
      const selected = activeFilters[f.id];
      if (!selected || selected.size === 0) return true;
      const value = (modelData?.variants?.[id] as any)?.[f.key];
      if (value === undefined) return true;
      return selected.has(String(value));
    });

  const renderPreviewList = () => (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto px-4 md:px-[23px] pt-[24px] pb-6 no-scrollbar scroll-pt-4">
        <div className="space-y-[17px]">
          <ControlledFeatureGroup videoElementId="features-video-element" className="space-y-[17px]">
            <PlayAllSection
              featureIDs={featureIDs}
              modelData={modelData}
              isDarkMode={isDarkMode}
              autoPlayOnMount={!hasAutoPlayedPreviewFeatures}
              onAutoPlayStarted={() => setHasAutoPlayedPreviewFeatures(true)}
            />
          </ControlledFeatureGroup>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    if (isPreviewMode) return renderPreviewList();
    switch (activeTab) {
      case 'CONFIG': {
        const activeFilterOptions = filterOptions.filter((f) => !f.disabled);
        const bodyStyles = (['Coupe', 'Convertible'] as const).filter((bs) => variantIDs.some((id) => bodyStyleOf(id) === bs));
        return (
          <div className="space-y-8">
            {/* Body Style — Coupe / Convertible cards (drives the model list) */}
            {bodyStyles.length > 1 && (
              <div className="space-y-4">
                <h1 className={cn('font-sans text-[14px] md:text-[16px] font-bold leading-[20px] tracking-[0.35px] uppercase', isDarkMode ? 'text-white' : 'text-[#212121]')}>
                  {t('staticTranslations.variantSelection.bodyStyle', 'Body Style')}
                </h1>
                <div className="flex flex-col gap-4">
                  {bodyStyles.map((bs) => {
                    const isSelected = bodyStyleFilter === bs;
                    const capacity = modelData?.variants?.[variantID]?.specifications?.seatingCapacity ?? '2';
                    return (
                      <div
                        key={bs}
                        onClick={() => setBodyStyleFilter(bs)}
                        className={cn(
                          'rounded-[var(--brand-card-radius-lg)] border-2 px-4 py-[26px] cursor-pointer transition-all',
                          isSelected
                            ? 'border-[var(--brand-accent)]'
                            : isDarkMode
                            ? 'border-white/5 bg-[#111111] hover:border-white/10'
                            : 'border-gray-100 bg-white hover:border-gray-200 shadow-sm',
                        )}
                      >
                        <div className="flex flex-col gap-[6px]">
                          <span className={cn('font-sans text-[24px] font-black leading-none', isDarkMode ? 'text-white' : 'text-black')}>
                            {bs === 'Coupe'
                              ? t('staticTranslations.variantSelection.coupe', 'Coupe')
                              : t('staticTranslations.variantSelection.convertible', 'Convertible')}
                          </span>
                          <span className={cn('font-sans text-[14px] font-medium', isDarkMode ? 'text-white/50' : 'text-gray-500')}>
                            {t('staticTranslations.variantSelection.passengerCapacity', 'Passenger Capacity')}: {capacity}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {activeFilterOptions.map((filter) => (
              <div key={filter.id} className="space-y-4">
                <h1 className={cn('font-sans text-[14px] md:text-[16px] font-bold leading-[20px] tracking-[0.35px] uppercase', isDarkMode ? 'text-white' : 'text-[#212121]')}>
                  {t('staticTranslations.variantSelection.select', 'SELECT')} {isRTL ? filter.label.ar : filter.label.en}
                </h1>
                <div className="flex flex-col gap-[24px]">
                  {filter.options.map((option: any) => (
                    <FilterOptionCard
                      key={option.id}
                      isActive={activeFilters[filter.id]?.has(option.value) ?? false}
                      option={option}
                      isDarkMode={isDarkMode}
                      onClick={() => {
                        if (/year/i.test(filter.id) || /year/i.test(filter.label?.en || '')) {
                          trackEvent(AnalyticsIds.modelYearInteract, {
                            primaryTypeName: String(option.value),
                            secondaryTypeName: String(
                              Array.from(activeFilters[filter.id] || [])[0] ?? ''
                            ),
                          });
                        }
                        toggleFilter(filter.id, option.value);
                      }}
                      isRTL={isRTL}
                    />
                  ))}
                </div>
              </div>
            ))}
            {activeFilterOptions.length === 0 && bodyStyles.length <= 1 && (
              <p className={cn('text-[13px]', isDarkMode ? 'text-white/40' : 'text-gray-400')}>
                {t('staticTranslations.configPanel.noFilters', 'No filters available.')}
              </p>
            )}
          </div>
        );
      }

      case 'MODEL': {
        // Collect unique drivetrains from all available variant IDs
        const allDrivetrains: string[] = [];
        variantIDs.forEach((id) => {
          const dt: string = modelData?.variants?.[id]?.drivetrain ?? modelData?.variants?.[id]?.description?.en ?? '';
          if (dt && !allDrivetrains.includes(dt)) allDrivetrains.push(dt);
        });

        // Default selectedDrivetrain to the current variant's drivetrain
        const currentDrivetrain: string =
          modelData?.variants?.[variantID]?.drivetrain ??
          modelData?.variants?.[variantID]?.description?.en ?? '';

        // Body style filter is selected in the CONFIG tab (bodyStyleFilter).
        const filteredVariantIDs = variantIDs.filter((id) => {
          const dt: string = modelData?.variants?.[id]?.drivetrain ?? modelData?.variants?.[id]?.description?.en ?? '';
          const dtMatch = modelId === 'Silverado' || modelId === 'Terrain' ? true : dt === (selectedDrivetrain || currentDrivetrain);
          const bsMatch = bodyStyleOf(id) === bodyStyleFilter;
          return dtMatch && bsMatch && matchesActiveFilters(id);
        });

        const handleDrivetrainSelect = async (dt: string) => {
          setSelectedDrivetrain(dt);
          // Find variant with same trim in new drivetrain
          const currentTrim: string = modelData?.variants?.[variantID]?.trim ?? modelData?.variants?.[variantID]?.name?.en ?? '';
          const candidates = variantIDs.filter((id) => {
            const vdt: string = modelData?.variants?.[id]?.drivetrain ?? modelData?.variants?.[id]?.description?.en ?? '';
            return vdt === dt && matchesActiveFilters(id);
          });
          const trimMatch = candidates.find((id) => {
            const trim: string = modelData?.variants?.[id]?.trim ?? modelData?.variants?.[id]?.name?.en ?? '';
            return trim === currentTrim;
          });
          const lowestIndex = candidates.sort((a, b) => (modelData?.variants?.[a]?.index ?? 0) - (modelData?.variants?.[b]?.index ?? 0))[0];
          const targetId = trimMatch ?? lowestIndex;
          if (targetId && targetId !== variantID) {
            const { setConfiguration } = ConfigurationStore.getState();
            setConfiguration(null);
            const configObject = await useChangeDefaultConfigOnVariantChange(uimetadata.config.model, targetId);
            if (configObject) setConfiguration(configObject);
          }
        };

        const handleVariantSelect = async (id: string) => {
          if (id === variantID) return;
          trackEvent(AnalyticsIds.variantInteract, {
            parent: 'modelTab',
            primaryTypeName: modelData?.variants?.[id]?.name?.en || id,
            secondaryTypeName: String(getPrice(modelData?.variants?.[variantID]?.price)),
            tertiaryTypeName: String(getPrice(modelData?.variants?.[id]?.price)),
            quaternaryTypeName: modelData?.variants?.[id]?.drivetrain || '',
          });
          const { setConfiguration } = ConfigurationStore.getState();
          // Abort any running feature autoplay before switching variants.
          FeatureStore.getState().setCurrentFeatureId(null);
          FeatureStore.getState().setStatus('play');
          // Close EVERY open interaction (doors, roof, boot) before switching so none
          // carries over open to the new variant. Closing via HandleInteraction also
          // flips the InteractionStore state, keeping the UI toggles in sync with the
          // graphics. snapToLastFrame => no visible close animation on the outgoing car.
          // (A post-load safety net in App.tsx re-asserts "all closed" after the switch.)
          const { interactions } = InteractionStore.getState();
          const openInteractionIds = Object.keys(interactions).filter(
            (interactionId) => interactions[interactionId].state,
          );
          if (openInteractionIds.length > 0) {
            await Promise.all(
              openInteractionIds.map((iid) => HandleInteraction(iid, false, { snapToLastFrame: true })),
            );
          }
          // Wait for any in-flight color/accessory load to settle before
          // switching. Starting the variant teardown while an exterior-color
          // SwapSku (or accessory reload) is still applying interleaves the
          // asset swap with the variant re-init, which can leave an
          // interaction (e.g. a door) visually stuck open on the new variant.
          // Bounded so a genuinely stuck load can't block the switch forever.
          const settleStart = Date.now();
          while (Date.now() - settleStart < 8000) {
            const ls = LoaderState.getState();
            if (!ls.isColorLoading && !ls.isAccessoryLoading && !ls.isInteractionLoading) break;
            await new Promise((r) => setTimeout(r, 100));
          }
          // Remove any applied packages BEFORE switching variants. The SDK's
          // variant-init tears down the outgoing variant's assets and, while
          // doing so, removes that variant's accessories - a package accessory
          // (e.g. the V-Series sunroof) cascades into re-adding its group's
          // default (WO_Sunroof) with state=true, which then waits for an
          // 'AccessoryLoaded' ack the engine never emits mid-teardown. That
          // await never resolves, so setVariantLoading(false) never runs and
          // the model is stuck on "LOADING 3D MODEL…". Removing the packages
          // now, while the current variant is still fully loaded (the same
          // path a manual package removal uses, which works fine), clears
          // those accessories so variant-init has nothing to hang on.
          const appliedPackageIDs = [...usePackageStore.getState().packageIDs];
          for (const pkgId of appliedPackageIDs) {
            await RemovePackage(pkgId, false);
          }
          // Wait for any color/accessory loads triggered by package removal
          // to settle before starting the variant switch. Package removal can
          // trigger interior color changes (e.g. reverting from a locked color
          // to the variant default), and if the variant switch starts while
          // those color swaps are still in-flight, the stale assetIdentifier
          // in the SkuStore can cause the wrong interior texture to load.
          const settleAfterPkgStart = Date.now();
          while (Date.now() - settleAfterPkgStart < 5000) {
            const ls = LoaderState.getState();
            if (!ls.isColorLoading && !ls.isAccessoryLoading && !ls.isInteractionLoading) break;
            await new Promise((r) => setTimeout(r, 100));
          }
          setConfiguration(null);
          const configObject = await useChangeDefaultConfigOnVariantChange(uimetadata.config.model, id);
          if (configObject) setConfiguration(configObject);
        };

        const activeDt = selectedDrivetrain || currentDrivetrain;

        return (
          <div className="space-y-4">
            {/* Drivetrain filter chips — hidden for Corvette (single drivetrain / body-style
                selection lives in the CONFIG tab), Silverado (drivetrain grouped under Cab Type),
                and Terrain (AWD/FWD Elevation split has no CONFIG-tab equivalent filter, so hiding
                the chip and always showing all variants avoids stranding Denali/AT4/Elevation); kept for other models. */}
            {allDrivetrains.length > 1 && modelId !== 'Corvette' && modelId !== 'Silverado' && modelId !== 'Terrain' && (
              <div className="space-y-2">
                <h1 className={cn('font-sans text-[14px] md:text-[16px] font-bold leading-[20px] tracking-[0.35px] uppercase', isDarkMode ? 'text-white' : 'text-[#212121]')}>
                  {t('staticTranslations.variantSelection.driveType', 'Drive Type')}
                </h1>
                <div className="flex gap-2">
                  {allDrivetrains.map((dt) => (
                    <button
                      key={dt}
                      onClick={() => handleDrivetrainSelect(dt)}
                      className={cn(
                        'flex-1 py-3 font-sans text-[14px] font-bold rounded-[var(--brand-button-radius)] border-2 transition-all',
                        activeDt === dt
                          ? 'border-[var(--brand-accent)] text-[var(--brand-accent)] bg-white'
                          : isDarkMode
                          ? 'border-white/10 text-white/50 bg-transparent hover:border-white/20'
                          : 'border-gray-200 text-gray-400 bg-gray-50 hover:border-gray-300',
                      )}
                    >
                      {dt}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Count label */}
            <div className="flex items-center gap-[4px]">
              <h2 className={cn('font-sans text-[14px] md:text-[16px] font-bold leading-[20px] tracking-[0.35px] uppercase', isDarkMode ? 'text-white' : 'text-[#212121]')}>
                {t('staticTranslations.variantSelection.model', 'Model')}
              </h2>
              <span className={cn('font-sans text-[14px] font-medium leading-[20px] tracking-[0.3184px] uppercase', isDarkMode ? 'text-white/40' : 'text-[#adadad]')}>
                ({filteredVariantIDs.length})
              </span>
            </div>

            {/* Variant cards */}
            <div className="flex flex-col gap-4">
              {filteredVariantIDs.map((id) => {
                const vd = modelData?.variants?.[id];
                if (!vd) return null;
                const isSelected = id === variantID;
                const trimName: string = isRTL ? (vd.name?.ar ?? vd.name?.en ?? '') : (vd.name?.en ?? '');
                const drivetrainLabel: string = vd.drivetrain ?? vd.description?.en ?? '';
                const price: number = getDisplayPrice(vd.price);

                return (
                  <div
                    key={id}
                    onClick={() => handleVariantSelect(id)}
                    className={cn(
                      'rounded-[var(--brand-card-radius-lg)] border-2 px-4 py-[26px] cursor-pointer transition-all',
                      isSelected
                        ? 'border-[var(--brand-accent)]'
                        : isDarkMode
                        ? 'border-white/5 bg-[#111111] hover:border-white/10'
                        : 'border-gray-100 bg-white hover:border-gray-200 shadow-sm',
                    )}
                  >
                    <div className="flex items-baseline gap-2">
                      <span className={cn('font-sans text-[24px] font-black leading-none', isDarkMode ? 'text-white' : 'text-black')}>{trimName}</span>
                      {drivetrainLabel && (
                        <span className={cn('font-sans text-[14px] font-medium', isDarkMode ? 'text-white/50' : 'text-gray-500')}>{drivetrainLabel}</span>
                      )}
                    </div>
                    {/* A trim's price is the whole car, not an upcharge — no + sign.
                        Unpriced trims show nothing. */}
                    {price > 0 && (
                      <span className={cn('block mt-[6px] font-sans text-[14px] font-medium whitespace-nowrap', isDarkMode ? 'text-white/50' : 'text-gray-500')}>
                        {formatPrice(price)}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {filteredVariantIDs.length === 0 && (
              <p className={cn('text-[13px]', isDarkMode ? 'text-white/40' : 'text-gray-400')}>
                {t('staticTranslations.configPanel.noVariantsMatch', 'No trims match the selected filters.')}
              </p>
            )}
          </div>
        );
      }

      case 'EXTERIOR': {
        const exteriorColorIds = colorsIDs?.exterior ?? [];
        const roofAccessoryIds = accessoryIDs.filter((id) => modelData?.accessories?.[id]?.category?.en === 'Roof');
        const exteriorCategories = categoriesForTab(modelData, 'exterior');
        return (
          <div className="space-y-3 md:space-y-4">
            <AccordionItem title={`${t('staticTranslations.configPanel.exteriorColors', 'Exterior Colors')} (${exteriorColorIds.length})`} isOpen={openSections.colors} onToggle={() => toggleSection('colors')} isDarkMode={isDarkMode} parent={activeTab}>
              <ColorGroup type="exterior" className="grid grid-cols-2 gap-x-[17px] gap-y-[14px] md:gap-x-[19px] md:gap-y-[16px]">
                {exteriorColorIds.map((id) => (
                  <Color key={id} colorId={id} type="exterior" onClick={handleColorClick}>
                    <ColorCard data={modelData?.colors?.exterior?.[id]} isDarkMode={isDarkMode} />
                  </Color>
                ))}
              </ColorGroup>
            </AccordionItem>
            {exteriorCategories
              ? exteriorCategories.map((cat) => {
                  const ids = getAccessoryIdsByCategory(modelId, variantID, cat, 'exterior', exteriorColorID, interiorColorID);
                  if (!ids.length) return null;
                  // Corvette's alloys are SKU assets swapped via SwapSku; every
                  // other project's are ordinary interaction accessories.
                  if (cat === 'Alloy Wheels' && ids.some((id) => CORVETTE_ALLOY_SKU[id])) {
                    return (
                      <AccordionItem key={cat} title={`${cat} (${ids.length})`} isOpen={openSections[cat] !== false} onToggle={() => { toggleSection(cat); }} isDarkMode={isDarkMode} parent={activeTab}>
                        <AlloyConfigurator ids={ids} modelData={modelData} variantID={variantID} isDarkMode={isDarkMode} />
                      </AccordionItem>
                    );
                  }
                  return (
                    <AccordionItem key={cat} title={`${cat} (${ids.length})`} isOpen={openSections[cat] !== false} onToggle={() => { toggleSection(cat); }} isDarkMode={isDarkMode} parent={activeTab}>
                      <AccessoryGroup className="grid grid-cols-2 gap-x-[17px] gap-y-[14px] md:gap-x-[19px] md:gap-y-[16px]">
                        {ids.map((id) => (
                          <Accessory key={id} accId={id} onClick={handleAccessoryClick}>
                            <AccessoryCard data={modelData?.accessories?.[id]} isDarkMode={isDarkMode} />
                          </Accessory>
                        ))}
                      </AccessoryGroup>
                    </AccordionItem>
                  );
                })
              : roofAccessoryIds.length > 0 && (
                  <AccordionItem title={`${t('staticTranslations.configPanel.roofOptions', 'Roof Options')} (${roofAccessoryIds.length})`} isOpen={openSections.accessories} onToggle={() => toggleSection('accessories')} isDarkMode={isDarkMode} parent={activeTab}>
                    <AccessoryGroup className="grid grid-cols-2 gap-x-[17px] gap-y-[14px] md:gap-x-[19px] md:gap-y-[16px]">
                      {roofAccessoryIds.map((id) => (
                        <Accessory key={id} accId={id} onClick={handleAccessoryClick}>
                          <AccessoryCard data={modelData?.accessories?.[id]} isDarkMode={isDarkMode} />
                        </Accessory>
                      ))}
                    </AccessoryGroup>
                  </AccordionItem>
                )}
          </div>
        );
      }

      case 'INTERIOR': {
        const interiorColorIds = colorsIDs?.interior ?? [];
        // Fallback for projects without an `accessoryCategories.interior` layout
        // (Traverse). Include interior-view accessories too: seat options are
        // `view: 'interior'` so applying one keeps the camera inside.
        const seatAccessoryIds = [...accessoryIDs, ...interiorAccessoryIDs].filter((id) => modelData?.accessories?.[id]?.category?.en === 'Seats');
        const interiorCategories = categoriesForTab(modelData, 'interior');
        return (
          <div className="space-y-3 md:space-y-4">
            <AccordionItem title={`${t('staticTranslations.configPanel.interiorOptions', 'Interior Options')} (${interiorColorIds.length})`} isOpen={openSections.interior} onToggle={() => toggleSection('interior')} isDarkMode={isDarkMode} parent={activeTab}>
              <ColorGroup type="interior" className="grid grid-cols-2 gap-x-[17px] gap-y-[14px] md:gap-x-[19px] md:gap-y-[16px]">
                {interiorColorIds.map((id) => (
                  <Color key={id} colorId={id} type="interior" onClick={handleColorClick}>
                    <ColorCard data={modelData?.colors?.interior?.[id]} isDarkMode={isDarkMode} />
                  </Color>
                ))}
              </ColorGroup>
            </AccordionItem>
            {modelData?.interiorConfig ? (
              <InteriorConfigurator interiorColorID={interiorColorID} modelData={modelData} isDarkMode={isDarkMode} />
            ) : interiorCategories ? (
              interiorCategories.map((cat) => {
                const ids = getAccessoryIdsByCategory(modelId, variantID, cat, 'interior', exteriorColorID, interiorColorID);
                if (!ids.length) return null;
                return (
                  <AccordionItem key={cat} title={`${cat} (${ids.length})`} isOpen={openSections[cat] !== false} onToggle={() => { toggleSection(cat); }} isDarkMode={!!isDarkMode} parent={activeTab}>
                    <AccessoryGroup className="grid grid-cols-2 gap-x-[17px] gap-y-[14px] md:gap-x-[19px] md:gap-y-[16px]">
                      {ids.map((id) => (
                        <Accessory key={id} accId={id} onClick={handleAccessoryClick}>
                          <AccessoryCard data={modelData?.accessories?.[id]} isDarkMode={isDarkMode} />
                        </Accessory>
                      ))}
                    </AccessoryGroup>
                  </AccordionItem>
                );
              })
            ) : (
              seatAccessoryIds.length > 0 && (
                <AccordionItem title={`${t('staticTranslations.configPanel.seatOptions', 'Seat Options')} (${seatAccessoryIds.length})`} isOpen={openSections.accessories} onToggle={() => toggleSection('accessories')} isDarkMode={isDarkMode} parent={activeTab}>
                  <AccessoryGroup className="grid grid-cols-2 gap-x-[17px] gap-y-[14px] md:gap-x-[19px] md:gap-y-[16px]">
                    {seatAccessoryIds.map((id) => (
                      <Accessory key={id} accId={id} onClick={handleAccessoryClick}>
                        <AccessoryCard data={modelData?.accessories?.[id]} isDarkMode={isDarkMode} />
                      </Accessory>
                    ))}
                  </AccessoryGroup>
                </AccordionItem>
              )
            )}
          </div>
        );
      }

      case 'OPTIONS': {
        const genAccessoryIds = [...accessoryIDs, ...interiorAccessoryIDs].filter((id) => modelData?.accessories?.[id]?.category?.en === 'Accessories');
        // Corvette models packages as accessories in the 'Packages' category.
        const packageAccessoryIds = modelData?.interiorConfig ? getAccessoryIdsByCategory(modelId, variantID, 'Packages', undefined, exteriorColorID, interiorColorID) : [];
        // Projects with an explicit category layout render one section per
        // category instead of the single "Accessories" group.
        const optionCategories = categoriesForTab(modelData, 'options');
        const optionSections = (optionCategories ?? [])
          .map((cat) => ({ cat, ids: getAccessoryIdsByCategory(modelId, variantID, cat, undefined, exteriorColorID, interiorColorID) }))
          .filter((s) => s.ids.length > 0);
        return (
          <div className="space-y-3 md:space-y-8">
            {activePackageIDs.length > 0 && (
              <AccordionItem title={t('staticTranslations.bottomDrawer.packages', 'Packages')} isOpen={openSections.packages} onToggle={() => toggleSection('packages')} isDarkMode={isDarkMode} parent={activeTab}>
                <PackageGroup className="space-y-4">
                  {activePackageIDs.map((id) => (
                    <Package key={id} packageId={id}>
                      <PackageCard data={modelData?.packages?.[id]} isDarkMode={isDarkMode} onInfoClick={onPackageInfoClick} />
                    </Package>
                  ))}
                </PackageGroup>
              </AccordionItem>
            )}
            {packageAccessoryIds.length > 0 && (
              <AccordionItem title={`${t('staticTranslations.bottomDrawer.packages', 'Packages')} (${packageAccessoryIds.length})`} isOpen={openSections.packages ?? true} onToggle={() => toggleSection('packages')} isDarkMode={isDarkMode} parent={activeTab}>
                <AccessoryGroup className="grid grid-cols-2 gap-x-[17px] gap-y-[14px] md:gap-x-[19px] md:gap-y-[16px]">
                  {packageAccessoryIds.map((id) => (
                    <Accessory key={id} accId={id} onClick={handleAccessoryClick}>
                      <AccessoryCard data={modelData?.accessories?.[id]} isDarkMode={isDarkMode} />
                    </Accessory>
                  ))}
                </AccessoryGroup>
              </AccordionItem>
            )}
            {optionCategories
              ? optionSections.map(({ cat, ids }) => (
                  <AccordionItem key={cat} title={`${cat} (${ids.length})`} isOpen={openSections[cat] !== false} onToggle={() => { toggleSection(cat); }} isDarkMode={!!isDarkMode} parent={activeTab}>
                    <AccessoryGroup className="grid grid-cols-2 gap-x-[17px] gap-y-[14px] md:gap-x-[19px] md:gap-y-[16px]">
                      {ids.map((id) => (
                        <Accessory key={id} accId={id} onClick={handleAccessoryClick}>
                          <AccessoryCard data={modelData?.accessories?.[id]} isDarkMode={isDarkMode} />
                        </Accessory>
                      ))}
                    </AccessoryGroup>
                  </AccordionItem>
                ))
              : genAccessoryIds.length > 0 && (
                  <AccordionItem title={t('staticTranslations.bottomDrawer.accessories', 'Accessories')} isOpen={openSections.accessories} onToggle={() => toggleSection('accessories')} isDarkMode={isDarkMode} parent={activeTab}>
                    <AccessoryGroup className="grid grid-cols-2 gap-x-[17px] gap-y-[14px] md:gap-x-[19px] md:gap-y-[16px]">
                      {genAccessoryIds.map((id) => (
                        <Accessory key={id} accId={id} onClick={handleAccessoryClick}>
                          <AccessoryCard data={modelData?.accessories?.[id]} isDarkMode={isDarkMode} />
                        </Accessory>
                      ))}
                    </AccessoryGroup>
                  </AccordionItem>
                )}
            {activePackageIDs.length === 0 && packageAccessoryIds.length === 0 && (optionCategories ? optionSections.length === 0 : genAccessoryIds.length === 0) && (
              <p className={cn('font-sans text-[14px] font-normal', isDarkMode ? 'text-white/40' : 'text-[#666]')}>{t('staticTranslations.configPanel.noAdditionalOptions', 'No additional options available for the selected trim.')}</p>
            )}
          </div>
        );
      }

      case 'SUMMARY': {
        const variantData = modelData?.variants?.[variantID];
        const variantName = isRTL ? (variantData?.name?.ar ?? variantData?.name?.en ?? variantID) : (variantData?.name?.en ?? variantID);
        // The breakdown's trim line is the car price — the same figure the header
        // shows as "Starting Price … MSRP" — so it follows the header's own gate
        // rather than the per-item `showPrices` toggle (which would zero it).
        const trimLinePrice = isCarPriceVisible() ? getPrice(variantData?.price) : 0;
        const extColor = modelData?.colors?.exterior?.[exteriorColorID];
        const intColor = modelData?.colors?.interior?.[interiorColorID];
        // Defaults must not appear in the summary. Graphics isDefault accessories are
        // already display:false, but the interior seat/belt/stitch/steering defaults are
        // display:true, so exclude them explicitly for the current interior color.
        const _ic = modelData?.interiorConfig?.[interiorColorID];
        const defaultInteriorIds = new Set<string>();
        if (_ic) {
          if (_ic.defaultSeatId) defaultInteriorIds.add(_ic.defaultSeatId);
          if (_ic.steeringWheelId) defaultInteriorIds.add(_ic.steeringWheelId);
          Object.values(_ic.seats ?? {}).forEach((s: any) => {
            if (s?.seatBelt?.default) defaultInteriorIds.add(s.seatBelt.default);
            if (s?.stitch?.default) defaultInteriorIds.add(s.stitch.default);
          });
        }
        const selectedAccSummary = (accessoriesIDs ?? [])
          .filter((id: string) => { const gAcc = (metadata as any)?.models?.[modelId]?.accessories?.[id]; return gAcc && gAcc.display === true && gAcc.isDefault !== true && !defaultInteriorIds.has(id); })
          .map((id: string) => ({ id, label: isRTL ? (modelData?.accessories?.[id]?.label?.ar ?? modelData?.accessories?.[id]?.label?.en ?? id) : (modelData?.accessories?.[id]?.label?.en ?? id), image: modelData?.accessories?.[id]?.image ?? null, price: getDisplayPrice(modelData?.accessories?.[id]?.price) }));
        const selectedPkgSummary = (selectedPackageIDs ?? []).filter((id: string) => { const pkg = modelData?.packages?.[id]; return pkg && pkg.display !== false; }).map((id: string) => ({ id, label: isRTL ? (modelData?.packages?.[id]?.label?.ar ?? modelData?.packages?.[id]?.label?.en ?? id) : (modelData?.packages?.[id]?.label?.en ?? id), description: isRTL ? (modelData?.packages?.[id]?.description?.ar ?? modelData?.packages?.[id]?.description?.en ?? '') : (modelData?.packages?.[id]?.description?.en ?? ''), image: modelData?.packages?.[id]?.image ?? null, price: getDisplayPrice(modelData?.packages?.[id]?.price) }));
        // Paint and interior trim are chargeable options, so they count toward
        // the summary total exactly as they do in the header (see App.tsx).
        const extPrice = getDisplayPrice(extColor?.price);
        const intPrice = getDisplayPrice(intColor?.price);
        const pricesVisible = arePricesVisible();
        // Sum of the lines actually shown below, so the Total always reconciles
        // with the breakdown — the trim line uses the car price (header gate).
        const totalPrice = trimLinePrice + extPrice + intPrice + selectedAccSummary.reduce((s: number, a: any) => s + a.price, 0) + selectedPkgSummary.reduce((s: number, p: any) => s + p.price, 0);
        // Per-item price shown on each summary card. Chargeable options render the
        // upcharge; no-cost ones render nothing rather than a "free"/"standard" label.
        const summaryPrice = (p: number) => (p > 0 ? formatPrice(p, { sign: true }) : '');
        const summaryPriceClass = cn('font-sans text-[12px] font-medium leading-normal whitespace-nowrap', isDarkMode ? 'text-white/50' : 'text-[#666]');
        const variantDrivetrain: string = variantData?.drivetrain ?? '';
        // `price` is the line amount for the breakdown's right-hand column. The trim
        // carries the car price itself, so it is absolute; everything else is an
        // upcharge and renders with a leading +.
        const summaryItems: Array<{ label?: string; value: string; price: number; absolute?: boolean }> = [
          { label: t('staticTranslations.configPanel.trim', 'Trim'), value: variantDrivetrain ? `${variantName} ${variantDrivetrain}` : variantName, price: trimLinePrice, absolute: true },
          ...(extColor ? [{ label: t('staticTranslations.localMatches.exterior', 'Exterior'), value: isRTL ? (extColor.label?.ar ?? extColor.label?.en ?? '') : (extColor.label?.en ?? ''), price: extPrice }] : []),
          ...(intColor ? [{ label: t('staticTranslations.localMatches.interior', 'Interior'), value: isRTL ? (intColor.label?.ar ?? intColor.label?.en ?? '') : (intColor.label?.en ?? ''), price: intPrice }] : []),
          ...selectedAccSummary.map((a: any) => ({ value: a.label, price: a.price })),
          ...selectedPkgSummary.map((p: any) => ({ value: p.label, price: p.price })),
        ];

        return (
          <div className="flex flex-col gap-[32px]">
            {/* Exterior Colors */}
            {extColor && (
              <div className="flex flex-col gap-[16px]">
                <p className={cn('font-sans text-[16px] font-bold tracking-[0.35px] uppercase leading-[20px]', isDarkMode ? 'text-white' : 'text-[#212121]')}>{t('staticTranslations.configPanel.exteriorColors', 'Exterior Colors')}</p>
                <div className={cn('flex items-center overflow-hidden h-[119px] rounded-[var(--brand-card-radius)]', isDarkMode ? 'bg-[#111] border-[1.2px] border-white/10' : 'bg-white border-[1.2px] border-[#e6e6e6]')}>
                  <div className="w-[135px] h-full flex-shrink-0 overflow-hidden relative flex items-center justify-center">
                    {/* Exterior renders are wide side profiles (~3.4:1) in a 1.13:1 box —
                        object-cover would crop off the front and rear, so contain them. */}
                    {extColor.image ? (
                      <img src={assetSrc(extColor.image)} alt={extColor.label?.en} className="w-full h-full object-contain" />
                    ) : extColor.hex ? (
                      <div className="w-full h-full" style={{ backgroundColor: extColor.hex }} />
                    ) : (
                      <div className={cn('w-full h-full', isDarkMode ? 'bg-white/10' : 'bg-gray-100')} />
                    )}
                    <div className="absolute top-[8px] right-[8px] w-[21px] h-[21px] bg-[var(--brand-accent)] rounded-full flex items-center justify-center">
                      <Check className="w-[12px] h-[12px] text-white stroke-[2]" />
                    </div>
                  </div>
                  <div className="flex-1 px-[14px] min-w-0 flex flex-col gap-[4px]">
                    <AutoScrollText text={isRTL ? (extColor.label?.ar ?? extColor.label?.en) : extColor.label?.en} className={cn('font-sans text-[14px] font-bold capitalize', isDarkMode ? 'text-white' : 'text-black')} />
                    <span className={summaryPriceClass}>{summaryPrice(extPrice)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Interior Colors */}
            {intColor && (
              <div className="flex flex-col gap-[16px]">
                <p className={cn('font-sans text-[16px] font-bold tracking-[0.35px] uppercase leading-[20px]', isDarkMode ? 'text-white' : 'text-[#212121]')}>{t('staticTranslations.configPanel.interiorColors', 'Interior Colors')}</p>
                <div className={cn('flex items-center overflow-hidden h-[119px] rounded-[var(--brand-card-radius)]', isDarkMode ? 'bg-[#111] border-[1.2px] border-white/10' : 'bg-white border-[1.2px] border-[#e6e6e6]')}>
                  <div className="w-[135px] h-full flex-shrink-0 overflow-hidden relative">
                    {intColor.image ? (
                      <img src={assetSrc(intColor.image)} alt={intColor.label?.en} className="w-full h-full object-cover" />
                    ) : intColor.hex ? (
                      <div className="w-full h-full" style={{ backgroundColor: intColor.hex }} />
                    ) : (
                      <div className={cn('w-full h-full', isDarkMode ? 'bg-white/10' : 'bg-gray-100')} />
                    )}
                    <div className="absolute top-[8px] right-[8px] w-[21px] h-[21px] bg-[var(--brand-accent)] rounded-full flex items-center justify-center">
                      <Check className="w-[12px] h-[12px] text-white stroke-[2]" />
                    </div>
                  </div>
                  <div className="flex-1 px-[14px] min-w-0 flex flex-col gap-[4px]">
                    <AutoScrollText text={isRTL ? (intColor.label?.ar ?? intColor.label?.en) : intColor.label?.en} className={cn('font-sans text-[14px] font-bold capitalize', isDarkMode ? 'text-white' : 'text-black')} />
                    <span className={summaryPriceClass}>{summaryPrice(intPrice)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Packages */}
            {selectedPkgSummary.length > 0 && (
              <div className="flex flex-col gap-[16px]">
                <p className={cn('font-sans text-[16px] font-bold tracking-[0.35px] uppercase leading-[20px]', isDarkMode ? 'text-white' : 'text-[#212121]')}>{t('staticTranslations.bottomDrawer.packages', 'Packages')}</p>
                {selectedPkgSummary.map((pkg: any) => (
                  <div key={pkg.id} className={cn('flex items-center overflow-hidden h-[119px] rounded-[var(--brand-card-radius)]', isDarkMode ? 'bg-[#111] border-[1.2px] border-white/10' : 'bg-white border-[1.2px] border-[#e6e6e6]')}>
                    <div className="w-[135px] h-full flex-shrink-0 overflow-hidden relative">
                      {pkg.image ? (
                        <img src={assetSrc(pkg.image)} alt={pkg.label} className="w-full h-full object-cover" />
                      ) : (
                        <div className={cn('w-full h-full', isDarkMode ? 'bg-white/10' : 'bg-gray-100')} />
                      )}
                      <div className="absolute top-[8px] right-[8px] w-[21px] h-[21px] bg-[var(--brand-accent)] rounded-full flex items-center justify-center">
                        <Check className="w-[12px] h-[12px] text-white stroke-[2]" />
                      </div>
                    </div>
                    <div className="flex-1 px-[14px] flex flex-col gap-[4px] min-w-0">
                      <AutoScrollText text={pkg.label} className={cn('font-sans text-[14px] font-bold capitalize leading-normal', isDarkMode ? 'text-white' : 'text-black')} />
                      {pkg.description && (
                        <AutoScrollText text={pkg.description} className={cn('font-sans text-[12px] font-normal capitalize leading-normal', isDarkMode ? 'text-white/60' : 'text-black')} />
                      )}
                      <span className={summaryPriceClass}>{summaryPrice(pkg.price)}</span>
                    </div>
                    <button
                      onClick={() => {
                        trackEvent(AnalyticsIds.summaryItemRemove, {
                          state: false,
                          parent: 'summary',
                          primaryTypeName: pkg.label,
                          secondaryTypeName: 'package',
                          tertiaryTypeName: String(pkg.price ?? ''),
                        });
                        const { isVariantLoading } = LoaderState.getState();
                        RemovePackage(pkg.id, isVariantLoading);
                      }}
                      className={cn('flex-shrink-0 mx-[10px] p-1 rounded transition-colors', isDarkMode ? 'text-white/30 hover:text-white/70' : 'text-[#999] hover:text-black')}
                      aria-label="Remove package"
                    >
                      <Trash2 className="w-[24px] h-[24px]" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Exterior Accessories */}
            {selectedAccSummary.length > 0 && (
              <div className="flex flex-col gap-[16px]">
                <p className={cn('font-sans text-[16px] font-bold tracking-[0.35px] uppercase leading-[20px]', isDarkMode ? 'text-white' : 'text-[#212121]')}>{t('staticTranslations.configPanel.exteriorAccessories', 'Exterior Accessories')}</p>
                {selectedAccSummary.map((acc: any) => (
                  <div key={acc.id} className={cn('flex items-center overflow-hidden h-[119px]', isDarkMode ? 'bg-[#111] border-[1.2px] border-white/10 rounded-[var(--brand-card-radius)]' : 'bg-white border-[0.5px] border-[#c4c4c4] rounded-[var(--brand-card-radius)]')}>
                    <div className="w-[135px] h-full flex-shrink-0 overflow-hidden flex items-center justify-center">
                      {acc.image ? (
                        <img src={assetSrc(acc.image)} alt={acc.label} className="w-full h-full object-contain" />
                      ) : (
                        <div className={cn('w-full h-full', isDarkMode ? 'bg-white/10' : 'bg-gray-100')} />
                      )}
                    </div>
                    <div className="flex-1 px-[14px] min-w-0 flex flex-col gap-[4px]">
                      <AutoScrollText text={acc.label} className={cn('font-sans text-[14px] font-bold capitalize leading-normal', isDarkMode ? 'text-white' : 'text-[#212121]')} />
                      <span className={summaryPriceClass}>{summaryPrice(acc.price)}</span>
                    </div>
                    <button
                      onClick={() => {
                        trackEvent(AnalyticsIds.summaryItemRemove, {
                          state: false,
                          parent: 'summary',
                          primaryTypeName: acc.label,
                          secondaryTypeName:
                            modelData?.accessories?.[acc.id]?.category?.en === 'Alloy Wheels'
                              ? 'wheel'
                              : 'accessory',
                          tertiaryTypeName: String(acc.price ?? ''),
                        });
                        const { isVariantLoading } = LoaderState.getState();
                        RemoveMultipleAccessoriesWithLoading([acc.id], 'accessory', isVariantLoading);
                      }}
                      className={cn('flex-shrink-0 mx-[10px] p-1 rounded transition-colors', isDarkMode ? 'text-white/30 hover:text-white/70' : 'text-[#999] hover:text-black')}
                      aria-label="Remove accessory"
                    >
                      <Trash2 className="w-[24px] h-[24px]" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Configuration Breakdown */}
            <div className="flex flex-col gap-[16px]">
              <p className={cn('font-sans text-[16px] font-bold tracking-[0.35px] uppercase leading-[20px]', isDarkMode ? 'text-white' : 'text-[#212121]')}>{t('staticTranslations.configPanel.configurationBreakdown', 'Configuration Breakdown')}</p>
              <div className={cn('px-[26px] py-[26px] rounded-[var(--brand-cta-radius)] transition-colors duration-300', isDarkMode ? 'border border-white/10 bg-[#111111]' : 'bg-white border-[1.2px] border-[#e6e6e6]')}>
                <h4 className={cn('font-sans text-[26px] font-bold uppercase tracking-[0.35px] mb-[26px]', isDarkMode ? 'text-white' : 'text-[#212121]')}>{(uimetadata.config as any).modelName ?? modelId}</h4>
                <div className="flex flex-col gap-[15px]">
                  {summaryItems.map((item, i) => (
                    <div key={i} className={cn('pb-[15px] flex items-baseline justify-between gap-3', i !== summaryItems.length - 1 && (isDarkMode ? 'border-b border-white/5' : 'border-b border-[#e0e0e0]'))}>
                      <span className={cn('font-sans text-[14px] font-medium tracking-[0.35px] leading-[20px] min-w-0', isDarkMode ? 'text-white/80' : 'text-[#212121]')}>
                        {item.label ? `${item.label} (${item.value})` : item.value}
                      </span>
                      <span className={cn('font-sans text-[14px] font-medium leading-[20px] whitespace-nowrap flex-shrink-0', isDarkMode ? 'text-white/50' : 'text-[#666]')}>
                        {item.absolute ? (item.price > 0 ? formatPrice(item.price) : '') : summaryPrice(item.price)}
                      </span>
                    </div>
                  ))}
                  {/* Total — makes the itemised lines above reconcile. Shown whenever
                      any line carries a price (trim car price or per-item prices). */}
                  {(pricesVisible || trimLinePrice > 0) && (
                  <div className={cn('pt-[4px] flex items-baseline justify-between gap-3')}>
                    <span className={cn('font-sans text-[14px] font-bold uppercase tracking-[0.35px] leading-[20px]', isDarkMode ? 'text-white' : 'text-[#212121]')}>
                      {t('staticTranslations.configPanel.total', 'Total')}
                    </span>
                    <span className={cn('font-sans text-[16px] font-bold leading-[20px] whitespace-nowrap', isDarkMode ? 'text-white' : 'text-[#212121]')}>
                      {formatPrice(totalPrice)}
                    </span>
                  </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} className="relative flex-1 min-h-0 flex flex-col overflow-hidden">
      {/* Color-loading overlay: blocks panel interaction so the user can't change
          the (interior) color again until the current swap finishes. */}
      {isColorLoading && (
        <div
          className={cn(
            'absolute inset-0 z-30 flex items-center justify-center cursor-wait backdrop-blur-[1px]',
            isDarkMode ? 'bg-[#0a0a0a]/40' : 'bg-white/40',
          )}
        >
          <div className="flex flex-col items-center gap-3">
            <div className="w-7 h-7 border-2 border-[var(--brand-accent)] border-t-transparent rounded-full animate-spin" />
            <span className={cn('text-[11px] font-medium uppercase tracking-wider', isDarkMode ? 'text-white/60' : 'text-gray-500')}>
              {getViewportLoaderText() ?? t('staticTranslations.viewport.applyingColor', 'Applying color…')}
            </span>
          </div>
        </div>
      )}
      {showPreviewButton && featureIDs.length > 0 && (
        <div className="hidden md:flex w-full h-[60px] bg-[#262626] items-center justify-between px-[22px] flex-shrink-0">
          <div className="flex items-baseline gap-[8px] min-w-0 flex-1 mr-3">
            <span className='font-sans text-white font-bold uppercase text-[14px] md:text-[16px] tracking-[0.56px] md:tracking-[0.64px] truncate'>
              {isRTL ? (modelData?.variants?.[variantID]?.name?.ar ?? modelData?.variants?.[variantID]?.name?.en ?? variantID) : (modelData?.variants?.[variantID]?.name?.en ?? variantID)}
            </span>
            {modelData?.variants?.[variantID]?.drivetrain && (
              <span className='font-sans text-white/50 font-bold uppercase text-[11px] md:text-[12px] tracking-[0.5px] whitespace-nowrap flex-shrink-0'>
                {modelData.variants[variantID].drivetrain}
              </span>
            )}

          </div>
          {isPreviewMode ? (
            <button
              onClick={() => {
                trackEvent(AnalyticsIds.featureInteract, {
                  parent: 'toggle',
                  primaryTypeName: 'buildPrice',
                  secondaryTypeName: activeTab,
                });
                setIsPreviewMode(false);
              }}
              className="bg-[#313131] border-[1.092px] border-solid border-[var(--brand-button-border)] h-[33px] md:h-[36px] flex items-center justify-center gap-[6px] px-3 rounded-[var(--brand-button-radius-sm)] w-[161px] md:w-auto flex-shrink-0"
            >
              <span className='font-sans text-white font-bold uppercase text-[14px] md:text-[16px] tracking-[0.56px] md:tracking-[0.64px] whitespace-nowrap'>{t('staticTranslations.buildAndPrice', 'Build & Price')}</span>
              <ExternalLink className="text-white flex-shrink-0" size={16} />
            </button>
          ) : (
            <button
              onClick={() => {
                trackEvent(AnalyticsIds.featureInteract, {
                  parent: 'toggle',
                  primaryTypeName: 'exploreFeatures',
                  secondaryTypeName: activeTab,
                });
                setIsPreviewMode(true);
              }}
              className="hidden md:flex bg-[#313131] border-[1.092px] border-solid border-[var(--brand-button-border)] h-[33px] md:h-[36px] items-center justify-center gap-[6px] px-3 rounded-[var(--brand-button-radius-sm)] flex-shrink-0"
            >
              <span className='font-sans text-white font-bold uppercase text-[14px] md:text-[16px] tracking-[0.56px] md:tracking-[0.64px] whitespace-nowrap'>{t('staticTranslations.exploreFeatures', 'Explore Features')}</span>
              <ExternalLink className="text-white flex-shrink-0" size={16} />
            </button>
          )}
        </div>
      )}
      {/* The actual #features-video-element lives in Viewport.tsx, positioned
          over the 3D viewer area rather than here in the side panel - see the
          comment there for why. */}
      <div className={cn('flex-1', isPreviewMode ? 'overflow-hidden' : 'overflow-y-auto')}>
        <div className={cn(isPreviewMode ? 'p-0 h-full' : 'px-[17px] pt-4 pb-4 md:pl-[35px] md:pr-[44px] md:pt-4 md:pb-8')}>
          {renderContent()}
        </div>
      </div>
    </div>
  );
};
